import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from "recharts";
import Header from "../header/header";
import Sidebar from "../sidebar/sidebar";
import * as API from "./carbonSequestrationApi"; // adjust relative path

/* ═══════════════════════════════════════════════════════════════════
   THEME
═══════════════════════════════════════════════════════════════════ */
const T = {
  primary:"#3F88A5", primaryDark:"#2d6b84", primaryDeep:"#1e4d61",
  primaryLight:"#e8f4f8", primaryMid:"#a8d4e4",
  success:"#2D9D5E", successLight:"#e6f7ee",
  warning:"#E8A020", warningLight:"#fef3e0",
  danger:"#D94F41", dangerLight:"#fdf0ef",
  text:"#0f2630", textMid:"#3a5a6a", textLight:"#7a9aaa",
  bg:"#f4f8fa", card:"#ffffff", border:"#d4e8f0", borderLight:"#eaf3f8",
};

/* ═══════════════════════════════════════════════════════════════════
   CHART FALLBACK DATA (used only for dashboard charts, not entity lists)
═══════════════════════════════════════════════════════════════════ */
const carbonTrendData = [];
const biomassBreakdown = [];
const treeHealthData = [];
const soilRadarData = [];
const PIE_COLORS = [T.primary, T.success, T.warning, T.danger, "#9b59b6"];

/* ═══════════════════════════════════════════════════════════════════
   ENTITY TYPE MAP  (for entity-documents)
═══════════════════════════════════════════════════════════════════ */
const ENTITY_TYPES = {
  project: "PROJECT",
  plot:    "PLOT",
  stratum: "STRATUM",
  tree:    "TREE",
  soil:    "SOIL_SAMPLE",
  carbon:  "CARBON_STOCK_CALCULATION",
  mgmt:    "MANAGEMENT_ACTIVITY",
  harvest: "HARVEST",
  leakage: "LEAKAGE",
  bio:     "BIODIVERSITY",
  qc:      "STRATUM_QC",
};

/* ═══════════════════════════════════════════════════════════════════
   CUSTOM HOOKS
═══════════════════════════════════════════════════════════════════ */
/** Generic data loader — call refresh() to re-fetch */
const useApiData = (fetcher, deps = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetch = useCallback(async () => {
    setLoading(true);
    const result = await fetcher();
    setData(Array.isArray(result) ? result : result ? [result] : []);
    setLoading(false);
  }, deps); // eslint-disable-line
  useEffect(() => { fetch(); }, [fetch]);
  return { data, loading, refresh: fetch, setData };
};

/* ═══════════════════════════════════════════════════════════════════
   ATOM COMPONENTS
═══════════════════════════════════════════════════════════════════ */
const Badge = ({ color, children }) => {
  const m = { green:{bg:T.successLight,text:T.success,border:"#b8e8cc"}, blue:{bg:T.primaryLight,text:T.primaryDark,border:T.primaryMid}, yellow:{bg:T.warningLight,text:"#b07010",border:"#f5d08a"}, red:{bg:T.dangerLight,text:T.danger,border:"#f0b8b4"}, gray:{bg:"#f0f4f6",text:"#5a7080",border:"#d0dde4"}, purple:{bg:"#f3eeff",text:"#6b3aad",border:"#c8a8f0"} };
  const c = m[color]||m.gray;
  return <span style={{ background:c.bg, color:c.text, border:`1px solid ${c.border}`, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600, letterSpacing:0.3, whiteSpace:"nowrap" }}>{children}</span>;
};

const Btn = ({ onClick, children, variant="primary", size="md", icon, full, disabled }) => {
  const s = { primary:{background:T.primary,color:"#fff",border:`1px solid ${T.primary}`}, outline:{background:"#fff",color:T.primary,border:`1px solid ${T.primary}`}, ghost:{background:"transparent",color:T.textMid,border:"1px solid transparent"}, danger:{background:T.danger,color:"#fff",border:`1px solid ${T.danger}`}, success:{background:T.success,color:"#fff",border:`1px solid ${T.success}`} };
  const sz = { sm:{padding:"5px 12px",fontSize:12}, md:{padding:"7px 15px",fontSize:13}, lg:{padding:"10px 20px",fontSize:14} };
  return <button onClick={onClick} disabled={disabled} style={{ ...s[variant]||s.primary, ...sz[size]||sz.md, borderRadius:8, fontWeight:600, cursor:disabled?"not-allowed":"pointer", display:"inline-flex", alignItems:"center", gap:5, fontFamily:"inherit", width:full?"100%":"auto", justifyContent:full?"center":"flex-start", transition:"opacity 0.15s", opacity:disabled?0.55:1 }} onMouseEnter={e=>!disabled&&(e.currentTarget.style.opacity="0.82")} onMouseLeave={e=>(e.currentTarget.style.opacity="1")}>{icon&&<span style={{fontSize:14}}>{icon}</span>}{children}</button>;
};

const Spinner = ({ size=18 }) => (
  <span style={{ display:"inline-block", width:size, height:size, border:`2px solid ${T.borderLight}`, borderTop:`2px solid ${T.primary}`, borderRadius:"50%", animation:"spin 0.6s linear infinite" }}/>
);

const LoadingOverlay = () => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"48px 24px", color:T.textLight, gap:10 }}>
    <Spinner size={22}/><span style={{ fontSize:13, fontWeight:600 }}>Loading…</span>
  </div>
);

const EmptyState = ({ icon="📭", message="No records found", sub, action }) => (
  <div style={{ textAlign:"center", padding:"44px 24px", color:T.textLight }}>
    <div style={{ fontSize:36, marginBottom:10 }}>{icon}</div>
    <div style={{ fontSize:14, fontWeight:700, color:T.textMid, marginBottom:4 }}>{message}</div>
    {sub&&<div style={{ fontSize:12, marginBottom:14 }}>{sub}</div>}
    {action}
  </div>
);

const DeleteBtn = ({ onDelete, label="Delete" }) => {
  const [confirm, setConfirm] = useState(false);
  if (confirm) return (
    <div style={{ display:"flex", gap:4, alignItems:"center" }}>
      <span style={{ fontSize:11, color:T.danger, fontWeight:600 }}>Sure?</span>
      <Btn variant="danger" size="sm" onClick={onDelete}>Yes</Btn>
      <Btn variant="ghost" size="sm" onClick={()=>setConfirm(false)}>No</Btn>
    </div>
  );
  return <Btn variant="ghost" size="sm" onClick={()=>setConfirm(true)}>🗑 {label}</Btn>;
};

const StatCard = ({ icon, label, value, sub, trend, color=T.primary }) => (
  <div style={{ background:T.card, borderRadius:12, padding:"16px 18px", border:`1px solid ${T.borderLight}`, boxShadow:"0 1px 8px rgba(63,136,165,0.06)", display:"flex", alignItems:"flex-start", gap:12, flex:1, minWidth:155 }}>
    <div style={{ width:38, height:38, borderRadius:10, background:`${color}18`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, fontSize:18 }}>{icon}</div>
    <div style={{ flex:1, minWidth:0 }}>
      <div style={{ fontSize:10, color:T.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:0.7, marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:20, fontWeight:800, color:T.text, lineHeight:1.1 }}>{value}</div>
      {sub&&<div style={{ fontSize:11, color:trend==="up"?T.success:trend==="down"?T.danger:T.textLight, marginTop:3, fontWeight:500 }}>{trend==="up"&&"↑ "}{trend==="down"&&"↓ "}{sub}</div>}
    </div>
  </div>
);

const ChartCard = ({ title, subtitle, height=220, children }) => (
  <div style={{ background:T.card, borderRadius:12, padding:"16px 18px", border:`1px solid ${T.borderLight}`, boxShadow:"0 1px 8px rgba(63,136,165,0.06)" }}>
    <div style={{ marginBottom:12 }}><div style={{ fontSize:13, fontWeight:700, color:T.text }}>{title}</div>{subtitle&&<div style={{ fontSize:11, color:T.textLight, marginTop:1 }}>{subtitle}</div>}</div>
    <div style={{ height }}>{children}</div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   GOOGLE MAPS — WKT polygon renderer (SRID 4326 / WGS84)
═══════════════════════════════════════════════════════════════════ */
const parseWKTPolygon = (wkt) => {
  if (!wkt || typeof wkt !== 'string') return null;
  const inner = wkt.match(/POLYGON\s*\(\s*\(\s*([^)]+)\s*\)/i);
  if (!inner) return null;
  const pts = inner[1].trim().split(/,\s*/).map(pair => {
    const [lng, lat] = pair.trim().split(/\s+/).map(Number);
    return { lat, lng };
  }).filter(p => !isNaN(p.lat) && !isNaN(p.lng));
  return pts.length >= 3 ? pts : null;
};

const parseWKTPoint = (wkt) => {
  if (!wkt) return null;
  const m = wkt.match(/POINT\s*\(\s*([^\s]+)\s+([^\s)]+)\s*\)/i);
  if (!m) return null;
  return { lng: Number(m[1]), lat: Number(m[2]) };
};

let gmapsLoadPromise = null;
const loadGoogleMaps = () => {
  if (window.google?.maps) return Promise.resolve();
  if (gmapsLoadPromise) return gmapsLoadPromise;
  gmapsLoadPromise = new Promise((resolve, reject) => {
    const key = window.GOOGLE_MAPS_API_KEY || process.env?.REACT_APP_GOOGLE_MAPS_KEY || '';
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return gmapsLoadPromise;
};

const useGoogleMaps = () => {
  const [loaded, setLoaded] = useState(!!window.google?.maps);
  useEffect(() => {
    if (window.google?.maps) { setLoaded(true); return; }
    loadGoogleMaps().then(() => setLoaded(true)).catch(() => setLoaded(false));
  }, []);
  return loaded;
};

const BoundaryMap = ({ boundary, centroid, label="GPS Boundary", showPoints=false, height=220 }) => {
  const mapsLoaded = useGoogleMaps();
  const mapRef = useRef(null);

  const paths  = useMemo(() => parseWKTPolygon(boundary), [boundary]);
  const center = useMemo(() => {
    const cp = parseWKTPoint(centroid);
    if (cp) return cp;
    if (paths?.length) {
      return {
        lat: paths.reduce((s,p) => s+p.lat, 0) / paths.length,
        lng: paths.reduce((s,p) => s+p.lng, 0) / paths.length,
      };
    }
    return { lat: 20.5937, lng: 78.9629 };
  }, [centroid, paths]);

  useEffect(() => {
    if (!mapsLoaded || !mapRef.current) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center, zoom: paths?.length ? 14 : 6, mapTypeId: 'satellite',
      mapTypeControl: true,
      mapTypeControlOptions: { style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU, position: window.google.maps.ControlPosition.TOP_RIGHT },
      streetViewControl: false, fullscreenControl: true, zoomControl: true,
    });
    if (paths?.length) {
      const polygon = new window.google.maps.Polygon({ paths, strokeColor:'#3F88A5', strokeOpacity:0.92, strokeWeight:2.5, fillColor:'#3F88A5', fillOpacity:0.22 });
      polygon.setMap(map);
      const bounds = new window.google.maps.LatLngBounds();
      paths.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, 24);
      const infoWindow = new window.google.maps.InfoWindow({ content:`<div style="font-size:13px;font-weight:700;padding:2px 4px">${label}</div>` });
      polygon.addListener('click', e => { infoWindow.setPosition(e.latLng); infoWindow.open(map); });
    }
    if (showPoints && centroid) {
      const pt = parseWKTPoint(centroid);
      if (pt) new window.google.maps.Marker({ position:pt, map, title:label });
    }
  }, [mapsLoaded, paths, center, label, showPoints, centroid]);

  if (!mapsLoaded) {
    return (
      <div style={{ height, background:"#e8f4f8", borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", gap:8, border:`1px solid ${T.border}`, color:T.textLight, fontSize:12, fontWeight:600 }}>
        <Spinner size={16}/> Loading map…
      </div>
    );
  }
  if (!boundary && !centroid) {
    return (
      <div style={{ height, background:"#f4f8fa", borderRadius:10, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8, border:`2px dashed ${T.border}`, color:T.textLight }}>
        <span style={{ fontSize:32 }}>📍</span>
        <span style={{ fontSize:12, fontWeight:600 }}>No GPS boundary defined</span>
        <span style={{ fontSize:11 }}>Add a WKT POLYGON in the edit form</span>
      </div>
    );
  }
  return <div ref={mapRef} style={{ width:"100%", height, borderRadius:10, overflow:"hidden", border:`1px solid ${T.border}` }}/>;
};

const MapCard = ({ title, subtitle, label, boundary, centroid, showPoints=false, height=280 }) => (
  <div style={{ background:T.card, borderRadius:12, padding:"16px 18px", border:`1px solid ${T.borderLight}`, boxShadow:"0 1px 8px rgba(63,136,165,0.06)" }}>
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{title}</div>
      {subtitle&&<div style={{ fontSize:11, color:T.textLight, marginTop:1 }}>{subtitle}</div>}
    </div>
    <div style={{ height }}>
      <BoundaryMap boundary={boundary} centroid={centroid} label={label} showPoints={showPoints} height={height}/>
    </div>
  </div>
);

const DataTable = ({ columns, data, onRowClick, actions }) => (
  <div style={{ overflowX:"auto", borderRadius:10, border:`1px solid ${T.borderLight}` }}>
    <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
      <thead><tr style={{ background:T.primaryLight, borderBottom:`2px solid ${T.border}` }}>
        {columns.map((c,i)=><th key={i} style={{ padding:"10px 13px", textAlign:"left", fontWeight:700, color:T.primaryDark, fontSize:11, textTransform:"uppercase", letterSpacing:0.5, whiteSpace:"nowrap" }}>{c.label}</th>)}
        {actions&&<th style={{ padding:"10px 13px", width:80 }}></th>}
      </tr></thead>
      <tbody>{data.map((row,ri)=>(
        <tr key={ri} onClick={()=>onRowClick&&onRowClick(row)} style={{ borderBottom:`1px solid ${T.borderLight}`, cursor:onRowClick?"pointer":"default" }} onMouseEnter={e=>e.currentTarget.style.background=T.primaryLight} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          {columns.map((c,ci)=><td key={ci} style={{ padding:"10px 13px", color:T.text, verticalAlign:"middle" }}>{c.render?c.render(row[c.key],row):(row[c.key]??"—")}</td>)}
          {actions&&<td style={{ padding:"6px 13px" }} onClick={e=>e.stopPropagation()}>{actions(row)}</td>}
        </tr>
      ))}</tbody>
    </table>
    {data.length===0&&<div style={{ textAlign:"center", padding:32, color:T.textLight, fontSize:13 }}>No records found</div>}
  </div>
);

const InfoGrid = ({ items }) => (
  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(185px, 1fr))", gap:9 }}>
    {items.map((item,i)=>(<div key={i} style={{ background:"#fff", borderRadius:8, padding:"10px 13px", border:`1px solid ${T.borderLight}` }}><div style={{ fontSize:10, color:T.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5, marginBottom:3 }}>{item.label}</div><div style={{ fontSize:13, fontWeight:600, color:T.text }}>{item.value??"—"}</div></div>))}
  </div>
);

const Breadcrumb = ({ crumbs }) => (
  <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, marginBottom:16 }}>
    {crumbs.map((c,i)=>(<span key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>{i>0&&<span style={{ color:T.border, fontSize:15 }}>›</span>}{c.onClick?<span onClick={c.onClick} style={{ color:T.primary, cursor:"pointer", fontWeight:500 }}>{c.label}</span>:<span style={{ color:T.text, fontWeight:600 }}>{c.label}</span>}</span>))}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   MODAL
═══════════════════════════════════════════════════════════════════ */
const Modal = ({ open, onClose, title, subtitle, children, width=580 }) => {
  if (!open) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(15,38,48,0.55)", zIndex:1000, display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto", backdropFilter:"blur(2px)" }} onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:T.card, borderRadius:16, width:"100%", maxWidth:width, boxShadow:"0 24px 64px rgba(0,0,0,0.22)", border:`1px solid ${T.borderLight}` }}>
        <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${T.borderLight}`, display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:T.text, fontFamily:"Syne, sans-serif" }}>{title}</div>
            {subtitle&&<div style={{ fontSize:12, color:T.textLight, marginTop:3 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:20, color:T.textLight, lineHeight:1, padding:2 }}>✕</button>
        </div>
        <div style={{ padding:"20px 24px 24px", maxHeight:"70vh", overflowY:"auto" }}>{children}</div>
      </div>
    </div>
  );
};

/* ─── Form Field Atoms ─── */
const Field = ({ label, required, children, half }) => (
  <div style={{ gridColumn:half?"span 1":"span 2" }}>
    <label style={{ display:"block", fontSize:11, fontWeight:700, color:T.textMid, textTransform:"uppercase", letterSpacing:0.5, marginBottom:5 }}>{label}{required&&<span style={{ color:T.danger, marginLeft:2 }}>*</span>}</label>
    {children}
  </div>
);
const inp = { width:"100%", padding:"8px 11px", borderRadius:7, border:`1px solid ${T.border}`, fontSize:13, color:T.text, background:"#fff", fontFamily:"inherit", outline:"none", boxSizing:"border-box" };
const Inp = (p) => <input style={inp} {...p}/>;
const Sel = ({ children, ...p }) => <select style={{ ...inp, cursor:"pointer" }} {...p}>{children}</select>;
const Tex = (p) => <textarea style={{ ...inp, resize:"vertical", minHeight:70 }} {...p}/>;
const FormGrid = ({ children }) => <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px 16px" }}>{children}</div>;
const FormSection = ({ title, children }) => (
  <div style={{ marginBottom:18 }}>
    <div style={{ fontSize:12, fontWeight:800, color:T.primary, textTransform:"uppercase", letterSpacing:0.8, marginBottom:10, paddingBottom:6, borderBottom:`1px solid ${T.borderLight}` }}>{title}</div>
    {children}
  </div>
);

const FormActions = ({ onClose, onSubmit, saving, label="Save" }) => (
  <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20, paddingTop:16, borderTop:`1px solid ${T.borderLight}` }}>
    <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
    <Btn variant="success" icon={saving?undefined:"✓"} onClick={onSubmit} disabled={saving}>{saving?<span style={{ display:"flex", alignItems:"center", gap:6 }}><Spinner size={14}/>Saving…</span>:label}</Btn>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   FORMS
═══════════════════════════════════════════════════════════════════ */
const useForm = (initial={}) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  const extractVal = e =>
    e !== null && e !== undefined && typeof e === 'object' && 'target' in e
      ? e.target.value
      : e;

  const set = k => e => {
    const val = extractVal(e);
    setForm(f => ({ ...f, [k]: val }));
  };
  const setNum = k => e => {
    const raw = extractVal(e);
    const val = raw === '' || raw == null ? undefined : Number(raw);
    setForm(f => ({ ...f, [k]: val }));
  };
  const reset = () => setForm(initial);
  return { form, set, setNum, saving, setSaving, reset };
};

/* ─── Project Form ─── */
const ProjectForm = ({ onClose, onRefresh, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || {});
  const submit = async () => {
    if (!form.projectId) return;
    setSaving(true);
    const r = isEdit ? await API.updateProject(initialData.id, form) : await API.createProject(form);
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Basic Information">
        <FormGrid>
          <Field label="Project ID" required half><Inp placeholder="PRJ-2024-005" value={form.projectId||''} onChange={set('projectId')}/></Field>
          <Field label="Official Project Name" half><Inp placeholder="e.g. Teak Agroforestry Phase 2" value={form.officialProjectName||''} onChange={set('officialProjectName')}/></Field>
          <Field label="Project Type" half><Sel value={form.projectType||''} onChange={set('projectType')}><option value="">Select type</option><option>ARR</option><option>IFM</option><option>REDD+</option><option>ALM</option></Sel></Field>
          <Field label="Total Area (ha)" half><Inp type="number" placeholder="0.00" value={form.totalAreaHectares||''} onChange={setNum('totalAreaHectares')}/></Field>
          <Field label="Project Start Date" half><Inp type="date" value={form.projectStartDate||''} onChange={set('projectStartDate')}/></Field>
          <Field label="Baseline Year" half><Inp type="number" placeholder="2020" value={form.baselineYear||''} onChange={setNum('baselineYear')}/></Field>
          <Field label="Monitoring Period Start" half><Inp type="date" value={form.monitoringPeriodStart||''} onChange={set('monitoringPeriodStart')}/></Field>
          <Field label="Monitoring Period End" half><Inp type="date" value={form.monitoringPeriodEnd||''} onChange={set('monitoringPeriodEnd')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Location & Climate">
        <FormGrid>
          <Field label="Climate Zone" half><Sel value={form.climateZone||''} onChange={set('climateZone')}><option value="">Select</option><option>Tropical Moist</option><option>Semi-Arid</option><option>Subtropical</option><option>Arid</option><option>Temperate</option></Sel></Field>
          <Field label="Soil Type" half><Inp placeholder="e.g. Laterite" value={form.soilType||''} onChange={set('soilType')}/></Field>
          <Field label="Avg Annual Rainfall (mm)" half><Inp type="number" value={form.averageAnnualRainfallMm||''} onChange={setNum('averageAnnualRainfallMm')}/></Field>
          <Field label="Avg Temperature (°C)" half><Inp type="number" value={form.averageTemperatureCelsius||''} onChange={setNum('averageTemperatureCelsius')}/></Field>
          <Field label="Elevation (m)" half><Inp type="number" value={form.elevationMeters||''} onChange={setNum('elevationMeters')}/></Field>
          <Field label="Baseline Assessment Date" half><Inp type="date" value={form.baselineAssessmentDate||''} onChange={set('baselineAssessmentDate')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="GPS Boundaries">
        <FormGrid>
          <Field label="GPS Boundary (WKT Polygon)"><Inp placeholder="POLYGON((lon lat, ...))" value={form.gpsBoundary||''} onChange={set('gpsBoundary')}/></Field>
          <Field label="Centroid (WKT Point)" half><Inp placeholder="POINT(lon lat)" value={form.centroid||''} onChange={set('centroid')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Management">
        <FormGrid>
          <Field label="Project Manager" half><Inp value={form.projectManager||''} onChange={set('projectManager')}/></Field>
          <Field label="Contact Information" half><Inp value={form.contactInformation||''} onChange={set('contactInformation')}/></Field>
          <Field label="Pre-Project Condition Description"><Tex value={form.preProjectConditionDescription||''} onChange={set('preProjectConditionDescription')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Project":"Create Project"}/>
    </div>
  );
};

/* ─── Plot Form ─── */
const PlotForm = ({ onClose, onRefresh, projects=[], preselectedProjectId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { projectId: preselectedProjectId });
  const submit = async () => {
    if (!form.plotId || !form.projectId || !form.landUseType) return;
    setSaving(true);
    const r = isEdit
      ? await API.updatePlot(initialData.id, { ...form, projectId: Number(form.projectId) })
      : await API.createPlot({ ...form, projectId: Number(form.projectId) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Basic Information">
        <FormGrid>
          <Field label="Plot ID" required half><Inp placeholder="PLT-001-D" value={form.plotId||''} onChange={set('plotId')}/></Field>
          <Field label="Parent Project" required half>
            <Sel value={form.projectId||''} onChange={set('projectId')}>
              <option value="">Select project</option>
              {projects.map(p=><option key={p.id} value={p.id}>{p.officialProjectName||p.projectId}</option>)}
            </Sel>
          </Field>
          <Field label="Land Use Type" required half><Sel value={form.landUseType||''} onChange={set('landUseType')}><option value="">Select</option><option>FOREST</option><option>AGRICULTURE</option><option>GRASSLAND</option></Sel></Field>
          <Field label="Area (ha)" required half><Inp type="number" value={form.areaHectares||''} onChange={setNum('areaHectares')}/></Field>
          <Field label="Previous Land Use" half><Inp value={form.previousLandUse||''} onChange={set('previousLandUse')}/></Field>
          <Field label="Year of Conversion" half><Inp type="number" value={form.yearOfConversion||''} onChange={setNum('yearOfConversion')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Vegetation">
        <FormGrid>
          <Field label="Vegetation Type" half><Inp value={form.vegetationType||''} onChange={set('vegetationType')}/></Field>
          <Field label="Planting Density (trees/ha)" half><Inp type="number" value={form.plantingDensityTreesPerHa||''} onChange={setNum('plantingDensityTreesPerHa')}/></Field>
          <Field label="Species Composition"><Tex value={form.speciesComposition||''} onChange={set('speciesComposition')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="GPS Boundaries">
        <FormGrid>
          <Field label="GPS Boundary (WKT Polygon)" required><Inp placeholder="POLYGON((lon lat, ...))" value={form.gpsBoundary||''} onChange={set('gpsBoundary')}/></Field>
          <Field label="Centroid (WKT Point)" half><Inp placeholder="POINT(lon lat)" value={form.centroid||''} onChange={set('centroid')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Plot":"Create Plot"}/>
    </div>
  );
};

/* ─── Stratum Form ─── */
const StratumForm = ({ onClose, onRefresh, projects=[], preselectedProjectId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { projectId: preselectedProjectId });
  const submit = async () => {
    if (!form.stratumName || !form.projectId || !form.areaHectares) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateStratum(initialData.id, { ...form, projectId: Number(form.projectId), areaHectares: Number(form.areaHectares) })
      : await API.createStratum({ ...form, projectId: Number(form.projectId), areaHectares: Number(form.areaHectares) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Stratum Information">
        <FormGrid>
          <Field label="Stratum Name" required half><Inp value={form.stratumName||''} onChange={set('stratumName')}/></Field>
          <Field label="Parent Project" required half>
            <Sel value={form.projectId||''} onChange={set('projectId')}>
              <option value="">Select project</option>
              {projects.map(p=><option key={p.id} value={p.id}>{p.officialProjectName||p.projectId}</option>)}
            </Sel>
          </Field>
          <Field label="Area (ha)" required half><Inp type="number" value={form.areaHectares||''} onChange={setNum('areaHectares')}/></Field>
          <Field label="Avg Tree Age (years)" half><Inp type="number" value={form.averageTreeAgeYears||''} onChange={setNum('averageTreeAgeYears')}/></Field>
          <Field label="Dominant Species" half><Inp value={form.dominantSpecies||''} onChange={set('dominantSpecies')}/></Field>
          <Field label="Predominant Soil Type" half><Inp value={form.predominantSoilType||''} onChange={set('predominantSoilType')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="GPS Boundaries">
        <FormGrid>
          <Field label="GPS Boundary (WKT Polygon)" required><Inp placeholder="POLYGON((lon lat, ...))" value={form.gpsBoundary||''} onChange={set('gpsBoundary')}/></Field>
          <Field label="Centroid (WKT Point)" half><Inp placeholder="POINT(lon lat)" value={form.centroid||''} onChange={set('centroid')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Stratum":"Create Stratum"}/>
    </div>
  );
};

/* ─── Tree Form ─── */
const TreeForm = ({ onClose, onRefresh, plotId, strata=[], initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.treeId || !form.speciesName || !form.dbhCm || !form.measurementDate) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateTree(initialData.id, { ...form, plotId: Number(form.plotId||plotId), dbhCm: Number(form.dbhCm) })
      : await API.createTree({ ...form, plotId: Number(form.plotId||plotId), dbhCm: Number(form.dbhCm) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Tree Identity">
        <FormGrid>
          <Field label="Tree ID" required half><Inp placeholder="TRE-007" value={form.treeId||''} onChange={set('treeId')}/></Field>
          <Field label="Species Name (Scientific)" required half><Inp value={form.speciesName||''} onChange={set('speciesName')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Measurements">
        <FormGrid>
          <Field label="DBH (cm)" required half><Inp type="number" step="0.01" value={form.dbhCm||''} onChange={setNum('dbhCm')}/></Field>
          <Field label="Height (m)" half><Inp type="number" step="0.01" value={form.heightMeters||''} onChange={setNum('heightMeters')}/></Field>
          <Field label="Age (years)" half><Inp type="number" step="0.1" value={form.ageYears||''} onChange={setNum('ageYears')}/></Field>
          <Field label="Crown Diameter (m)" half><Inp type="number" step="0.01" value={form.crownDiameterMeters||''} onChange={setNum('crownDiameterMeters')}/></Field>
          <Field label="Year Planted" half><Inp type="number" value={form.yearPlanted||''} onChange={setNum('yearPlanted')}/></Field>
          <Field label="Measurement Date" required half><Inp type="date" value={form.measurementDate||''} onChange={set('measurementDate')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Status">
        <FormGrid>
          <Field label="Health Status" half><Sel value={form.healthStatus||''} onChange={set('healthStatus')}><option value="">Select</option><option>GOOD</option><option>FAIR</option><option>POOR</option></Sel></Field>
          <Field label="Life Status" half><Sel value={form.lifeStatus||''} onChange={set('lifeStatus')}><option value="">Select</option><option>LIVE</option><option>DEAD</option></Sel></Field>
          <Field label="GPS Location (WKT Point)" half><Inp placeholder="POINT(lon lat)" value={form.gpsLocation||''} onChange={set('gpsLocation')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Tree":"Add Tree"}/>
    </div>
  );
};

/* ─── Soil Sample Form ─── */
const SoilSampleForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.sampleId || !form.samplingDate || !form.depth) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateSoilSample(initialData.id, { ...form, plotId: Number(plotId) })
      : await API.createSoilSample({ ...form, plotId: Number(plotId) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Sample Identity">
        <FormGrid>
          <Field label="Sample ID" required half><Inp placeholder="SS-004" value={form.sampleId||''} onChange={set('sampleId')}/></Field>
          <Field label="Sampling Date" required half><Inp type="date" value={form.samplingDate||''} onChange={set('samplingDate')}/></Field>
          <Field label="Depth" required half><Sel value={form.depth||''} onChange={set('depth')}><option value="">Select depth</option><option value="0-30">0-30 cm</option><option value="30-60">30-60 cm</option></Sel></Field>
          <Field label="GPS Location (WKT Point)" half><Inp placeholder="POINT(lon lat)" value={form.gpsLocation||''} onChange={set('gpsLocation')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Lab Results">
        <FormGrid>
          <Field label="SOC %" half><Inp type="number" step="0.001" value={form.soilOrganicCarbonPercent||''} onChange={setNum('soilOrganicCarbonPercent')}/></Field>
          <Field label="Bulk Density (g/cm³)" half><Inp type="number" step="0.001" value={form.bulkDensityGPerCm3||''} onChange={setNum('bulkDensityGPerCm3')}/></Field>
          <Field label="SOM %" half><Inp type="number" step="0.001" value={form.soilOrganicMatterPercent||''} onChange={setNum('soilOrganicMatterPercent')}/></Field>
          <Field label="pH" half><Inp type="number" step="0.01" value={form.ph||''} onChange={setNum('ph')}/></Field>
          <Field label="Nitrogen %" half><Inp type="number" step="0.001" value={form.nitrogenPercent||''} onChange={setNum('nitrogenPercent')}/></Field>
          <Field label="Phosphorus (ppm)" half><Inp type="number" step="0.01" value={form.phosphorusPpm||''} onChange={setNum('phosphorusPpm')}/></Field>
          <Field label="Lab Analysis Date" half><Inp type="date" value={form.labAnalysisDate||''} onChange={set('labAnalysisDate')}/></Field>
          <Field label="Lab Method" half><Sel value={form.labMethod||''} onChange={set('labMethod')}><option value="">Select</option><option>Walkley-Black</option><option>Dry Combustion</option><option>Wet Oxidation</option><option>Loss on Ignition</option></Sel></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Soil Sample":"Add Soil Sample"}/>
    </div>
  );
};

/* ─── Carbon Stock Form ─── */
const CarbonStockForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.calculationDate || !form.totalCarbonStockTcPerHa) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateCarbonStock(initialData.id, { ...form, plotId: Number(plotId) })
      : await API.createCarbonStock({ ...form, plotId: Number(plotId) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Calculation Metadata">
        <FormGrid>
          <Field label="Calculation Date" required half><Inp type="date" value={form.calculationDate||''} onChange={set('calculationDate')}/></Field>
          <Field label="Methodology" half><Inp placeholder="e.g. VCS ARR v4.0" value={form.methodology||''} onChange={set('methodology')}/></Field>
          <Field label="Version" half><Inp type="number" value={form.version||''} onChange={setNum('version')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Biomass Components (tC/ha)">
        <FormGrid>
          <Field label="Above Ground Biomass" half><Inp type="number" step="0.0001" value={form.aboveGroundBiomassTcPerHa||''} onChange={setNum('aboveGroundBiomassTcPerHa')}/></Field>
          <Field label="Below Ground Biomass" half><Inp type="number" step="0.0001" value={form.belowGroundBiomassTcPerHa||''} onChange={setNum('belowGroundBiomassTcPerHa')}/></Field>
          <Field label="Dead Wood Biomass" half><Inp type="number" step="0.0001" value={form.deadwoodBiomassTcPerHa||''} onChange={setNum('deadwoodBiomassTcPerHa')}/></Field>
          <Field label="Litter Biomass" half><Inp type="number" step="0.0001" value={form.litterBiomassTcPerHa||''} onChange={setNum('litterBiomassTcPerHa')}/></Field>
          <Field label="Soil Organic Carbon" half><Inp type="number" step="0.0001" value={form.soilOrganicCarbonTcPerHa||''} onChange={setNum('soilOrganicCarbonTcPerHa')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Totals & Credits">
        <FormGrid>
          <Field label="Total Carbon Stock (tC/ha)" required half><Inp type="number" step="0.0001" value={form.totalCarbonStockTcPerHa||''} onChange={setNum('totalCarbonStockTcPerHa')}/></Field>
          <Field label="Carbon Stock Change (tC/ha/yr)" half><Inp type="number" step="0.0001" value={form.carbonStockChangeTcPerHaPerYear||''} onChange={setNum('carbonStockChangeTcPerHaPerYear')}/></Field>
          <Field label="Net tCO₂e/ha" half><Inp type="number" step="0.0001" value={form.netTco2ePerHa||''} onChange={setNum('netTco2ePerHa')}/></Field>
          <Field label="Uncertainty %" half><Inp type="number" step="0.01" value={form.uncertaintyPercent||''} onChange={setNum('uncertaintyPercent')}/></Field>
          <Field label="Uncertainty Adjusted Credits" half><Inp type="number" value={form.uncertaintyAdjustedCredits||''} onChange={setNum('uncertaintyAdjustedCredits')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Calculation":"Save Calculation"}/>
    </div>
  );
};

/* ─── Management Activity Form ─── */
const ManagementActivityForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.activityType || !form.activityDate || !form.areaAffectedHectares) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateManagementActivity(initialData.id, { ...form, plotId: Number(plotId), areaAffectedHectares: Number(form.areaAffectedHectares) })
      : await API.createManagementActivity({ ...form, plotId: Number(plotId), areaAffectedHectares: Number(form.areaAffectedHectares) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Activity Details">
        <FormGrid>
          <Field label="Activity Type" required half><Sel value={form.activityType||''} onChange={set('activityType')}><option value="">Select</option><option>PLANTING</option><option>PRUNING</option><option>HARVESTING</option><option>FERTILIZATION</option></Sel></Field>
          <Field label="Activity Date" required half><Inp type="date" value={form.activityDate||''} onChange={set('activityDate')}/></Field>
          <Field label="Area Affected (ha)" required half><Inp type="number" step="0.01" value={form.areaAffectedHectares||''} onChange={setNum('areaAffectedHectares')}/></Field>
          <Field label="Cost (₹)" half><Inp type="number" value={form.costInInr||''} onChange={setNum('costInInr')}/></Field>
          <Field label="Description"><Tex value={form.description||''} onChange={set('description')}/></Field>
          <Field label="Materials Used"><Tex value={form.materialsUsed||''} onChange={set('materialsUsed')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Carbon Impact">
        <FormGrid>
          <Field label="Expected Carbon Impact" half><Sel value={form.expectedCarbonImpact||''} onChange={set('expectedCarbonImpact')}><option value="">Select</option><option>POSITIVE</option><option>NEGATIVE</option><option>NEUTRAL</option></Sel></Field>
          <Field label="Est. Carbon Retention %" half><Inp type="number" step="0.01" value={form.estimatedCarbonRetentionPercent||''} onChange={setNum('estimatedCarbonRetentionPercent')}/></Field>
          <Field label="Affected Area Boundary (WKT)"><Inp placeholder="POLYGON((lon lat, ...))" value={form.affectedAreaBoundary||''} onChange={set('affectedAreaBoundary')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Activity":"Log Activity"}/>
    </div>
  );
};

/* ─── Harvest Record Form ─── */
const HarvestForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.speciesHarvested || !form.harvestDate || !form.volumeOrWeight || !form.unit) return;
    setSaving(true);
    const payload = { ...form, plotId: Number(plotId), volumeOrWeight: Number(form.volumeOrWeight), longTermStorageFractionPercent: Number(form.longTermStorageFractionPercent||0) };
    const r = isEdit ? await API.updateHarvestRecord(initialData.id, payload) : await API.createHarvestRecord(payload);
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Harvest Details">
        <FormGrid>
          <Field label="Species Harvested" required half><Inp value={form.speciesHarvested||''} onChange={set('speciesHarvested')}/></Field>
          <Field label="Harvest Date" required half><Inp type="date" value={form.harvestDate||''} onChange={set('harvestDate')}/></Field>
          <Field label="Product Type" required half><Inp value={form.productType||''} onChange={set('productType')}/></Field>
          <Field label="Volume or Weight" required half><Inp type="number" step="0.0001" value={form.volumeOrWeight||''} onChange={setNum('volumeOrWeight')}/></Field>
          <Field label="Unit" required half><Sel value={form.unit||''} onChange={set('unit')}><option value="">Select</option><option>M3</option><option>TONNES</option><option>TONNES_PER_HA</option></Sel></Field>
          <Field label="LT Storage Fraction %" required half><Inp type="number" step="0.01" min="0" max="100" value={form.longTermStorageFractionPercent||''} onChange={setNum('longTermStorageFractionPercent')}/></Field>
          <Field label="Notes"><Tex value={form.notes||''} onChange={set('notes')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Record":"Add Harvest Record"}/>
    </div>
  );
};

/* ─── Monitoring Schedule Form ─── */
const MonitoringForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.monitoringType || !form.scheduledDate || !form.personnelResponsible) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateMonitoringSchedule(initialData.id, { ...form, plotId: Number(plotId) })
      : await API.createMonitoringSchedule({ ...form, plotId: Number(plotId) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Schedule Details">
        <FormGrid>
          <Field label="Monitoring Type" required half><Sel value={form.monitoringType||''} onChange={set('monitoringType')}><option value="">Select</option><option>TREE</option><option>SOIL</option><option>BIOMASS</option><option>BIODIVERSITY</option></Sel></Field>
          <Field label="Scheduled Date" required half><Inp type="date" value={form.scheduledDate||''} onChange={set('scheduledDate')}/></Field>
          <Field label="Actual Date" half><Inp type="date" value={form.actualDate||''} onChange={set('actualDate')}/></Field>
          <Field label="Personnel Responsible" required half><Inp value={form.personnelResponsible||''} onChange={set('personnelResponsible')}/></Field>
          <Field label="Status" half><Sel value={form.status||''} onChange={set('status')}><option value="">Select</option><option>PENDING</option><option>COMPLETED</option><option>MISSED</option><option>RESCHEDULED</option></Sel></Field>
          <Field label="Notes"><Tex value={form.notes||''} onChange={set('notes')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Schedule":"Save Schedule"}/>
    </div>
  );
};

/* ─── Emission Leakage Form ─── */
const EmissionLeakageForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.emissionSource || !form.emissionDate || !form.activityDescription || !form.estimatedEmissionsTco2e) return;
    setSaving(true);
    const payload = { ...form, plotId: Number(plotId), quantity: Number(form.quantity||0), estimatedEmissionsTco2e: Number(form.estimatedEmissionsTco2e), leakageEstimateTco2e: form.leakageEstimateTco2e ? Number(form.leakageEstimateTco2e) : undefined };
    const r = isEdit ? await API.updateEmissionLeakage(initialData.id, payload) : await API.createEmissionLeakage(payload);
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Emission Source">
        <FormGrid>
          <Field label="Emission Source" required half>
            <Sel value={form.emissionSource||''} onChange={set('emissionSource')}>
              <option value="">Select source</option>
              <option>MACHINERY</option><option>TRANSPORT</option><option>FERTILIZER</option><option>BURNING</option><option>OTHER</option>
            </Sel>
          </Field>
          <Field label="Emission Date" required half><Inp type="date" value={form.emissionDate||''} onChange={set('emissionDate')}/></Field>
          <Field label="Activity Description" required><Tex placeholder="Describe the activity causing this emission..." value={form.activityDescription||''} onChange={set('activityDescription')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Fuel / Material">
        <FormGrid>
          <Field label="Fuel / Material Type" required half><Inp placeholder="e.g. Diesel, N-Fertilizer, Biomass" value={form.fuelOrMaterialType||''} onChange={set('fuelOrMaterialType')}/></Field>
          <Field label="Quantity" required half><Inp type="number" step="0.0001" value={form.quantity||''} onChange={setNum('quantity')}/></Field>
          <Field label="Unit" required half><Inp placeholder="Liters / Kg / Tonnes" value={form.unit||''} onChange={set('unit')}/></Field>
        </FormGrid>
      </FormSection>
      <FormSection title="Emissions Estimate">
        <FormGrid>
          <Field label="Estimated Emissions (tCO₂e)" required half><Inp type="number" step="0.0001" value={form.estimatedEmissionsTco2e||''} onChange={setNum('estimatedEmissionsTco2e')}/></Field>
          <Field label="Leakage Estimate (tCO₂e)" half><Inp type="number" step="0.0001" placeholder="Optional – often 0" value={form.leakageEstimateTco2e||''} onChange={setNum('leakageEstimateTco2e')}/></Field>
          <Field label="Notes"><Tex value={form.notes||''} onChange={set('notes')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Assessment":"Save Assessment"}/>
    </div>
  );
};

/* ─── Biodiversity Form ─── */
const BiodiversityForm = ({ onClose, onRefresh, plotId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { plotId });
  const submit = async () => {
    if (!form.indicatorType || !form.observationDate || !form.speciesObserved) return;
    setSaving(true);
    const r = isEdit
      ? await API.updateBiodiversityObservation(initialData.id, { ...form, plotId: Number(plotId) })
      : await API.createBiodiversityObservation({ ...form, plotId: Number(plotId) });
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="Observation Details">
        <FormGrid>
          <Field label="Indicator Type" required half><Sel value={form.indicatorType||''} onChange={set('indicatorType')}><option value="">Select</option><option>FLORA</option><option>FAUNA</option><option>BIRDS</option><option>INSECTS</option></Sel></Field>
          <Field label="Observation Date" required half><Inp type="date" value={form.observationDate||''} onChange={set('observationDate')}/></Field>
          <Field label="Species Observed (Scientific)" required half><Inp value={form.speciesObserved||''} onChange={set('speciesObserved')}/></Field>
          <Field label="Count / Abundance" half><Inp type="number" value={form.countOrAbundance||''} onChange={setNum('countOrAbundance')}/></Field>
          <Field label="Assessment Method" half><Sel value={form.assessmentMethod||''} onChange={set('assessmentMethod')}><option value="">Select</option><option>TRANSECT</option><option>PLOT</option><option>CAMERA</option><option>OTHER</option></Sel></Field>
          <Field label="Notes"><Tex value={form.notes||''} onChange={set('notes')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update Observation":"Add Observation"}/>
    </div>
  );
};

/* ─── Stratum QC Form ─── */
const StratumQCForm = ({ onClose, onRefresh, strata=[], preselectedStratumId, initialData }) => {
  const isEdit = !!initialData?.id;
  const { form, set, setNum, saving, setSaving } = useForm(initialData || { stratumId: preselectedStratumId });
  const submit = async () => {
    if (!form.stratumId || !form.assessmentDate) return;
    setSaving(true);
    const payload = { ...form, stratumId: Number(form.stratumId), uncertaintyPercent: form.uncertaintyPercent ? Number(form.uncertaintyPercent) : undefined, completenessChecklistPassed: form.completenessChecklistPassed === 'true' || form.completenessChecklistPassed === true };
    const r = isEdit ? await API.updateStratumQC(initialData.id, payload) : await API.createStratumQC(payload);
    setSaving(false);
    if (r) { onClose(); onRefresh?.(); }
  };
  return (
    <div>
      <FormSection title="QC Assessment">
        <FormGrid>
          {!preselectedStratumId && (
            <Field label="Stratum" required half>
              <Sel value={form.stratumId||''} onChange={set('stratumId')}>
                <option value="">Select stratum</option>
                {strata.map(s=><option key={s.id} value={s.id}>{s.stratumName}</option>)}
              </Sel>
            </Field>
          )}
          <Field label="Assessment Date" required half><Inp type="date" value={form.assessmentDate||''} onChange={set('assessmentDate')}/></Field>
          <Field label="Uncertainty %" half><Inp type="number" step="0.01" value={form.uncertaintyPercent||''} onChange={setNum('uncertaintyPercent')}/></Field>
          <Field label="Completeness Checklist" required half>
            <Sel value={form.completenessChecklistPassed||''} onChange={set('completenessChecklistPassed')}>
              <option value="">Select</option><option value="true">PASSED</option><option value="false">FAILED</option>
            </Sel>
          </Field>
          <Field label="System Flags"><Tex value={form.systemFlags||''} onChange={set('systemFlags')}/></Field>
          <Field label="Notes"><Tex value={form.notes||''} onChange={set('notes')}/></Field>
        </FormGrid>
      </FormSection>
      <FormActions onClose={onClose} onSubmit={submit} saving={saving} label={isEdit?"Update QC":"Submit QC"}/>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   IMAGE GALLERY — loads IMAGE-type entity documents from API
═══════════════════════════════════════════════════════════════════ */
const PREVIEW_GRADS = [
  { from:"#1e4d61",to:"#3F88A5",icon:"🌿" },{ from:"#2D9D5E",to:"#52c988",icon:"📷" },
  { from:"#E8A020",to:"#f5c05a",icon:"🌳" },{ from:"#7c4dbc",to:"#a77de0",icon:"🍂" },
];

const ImageGallery = ({ entityType, entityId }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const loadImages = async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    const docs = await API.getEntityDocuments(entityType, entityId);
    setImages((docs || []).filter(d => d.documentType === 'IMAGE'));
    setLoading(false);
  };

  useEffect(() => { loadImages(); }, [entityType, entityId]);

  const handleUploadMultiple = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    for (const file of Array.from(fileList)) {
      await API.uploadEntityDocument(file, {
        documentType: 'IMAGE',
        entityType,
        entityId,
        documentDate: new Date().toISOString().split('T')[0],
      });
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    loadImages();
  };

  const handleDelete = async (docId) => {
    await API.deleteEntityDocument(docId);
    if (selected?.id === docId) setSelected(null);
    loadImages();
  };

  const getUrl = doc => doc?.file?.url || doc?.file?.signedUrl || null;

  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.text }}>
          📷 Field Images {images.length > 0 && <Badge color="blue">{images.length}</Badge>}
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={e => handleUploadMultiple(e.target.files)}/>
          <Btn variant="outline" size="sm" icon={uploading ? "" : "⬆️"} onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <><Spinner size={12}/>Uploading…</> : "Upload Images"}
          </Btn>
        </div>
      </div>
      {loading ? <div style={{ padding:20, textAlign:"center" }}><Spinner/></div> : (
        <div style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:4 }}>
          {images.length === 0 && (
            <div style={{ color:T.textLight, fontSize:12, padding:"18px 0", fontStyle:"italic" }}>No images yet — upload the first one.</div>
          )}
          {images.map((img, idx) => {
            const g = PREVIEW_GRADS[idx % PREVIEW_GRADS.length];
            const url = getUrl(img);
            return (
              <div key={img.id} onClick={() => setSelected(selected?.id === img.id ? null : img)}
                style={{ flexShrink:0, width:160, borderRadius:10, overflow:"hidden", position:"relative",
                  border:`2px solid ${selected?.id===img.id ? T.primary : T.borderLight}`,
                  cursor:"pointer", transition:"border-color 0.15s" }}>
                <div style={{ height:100,
                  background: url ? `url(${url}) center/cover` : `linear-gradient(135deg, ${g.from}, ${g.to})`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:32, opacity:0.7, backgroundSize:"cover" }}>
                  {!url && g.icon}
                </div>
                <button onClick={e => { e.stopPropagation(); handleDelete(img.id); }} title="Delete image"
                  style={{ position:"absolute", top:6, right:6, width:22, height:22, borderRadius:"50%",
                    border:"none", background:"rgba(0,0,0,0.55)", color:"#fff", fontSize:12, cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center", lineHeight:1, fontWeight:700, padding:0 }}>
                  ✕
                </button>
                <div style={{ padding:"7px 9px", background:T.card }}>
                  <div style={{ fontSize:11, fontWeight:600, color:T.text, marginBottom:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {img.kpi || `Image ${idx + 1}`}
                  </div>
                  <div style={{ fontSize:10, color:T.textLight }}>{img.documentDate || img.createdAt?.split('T')[0]}</div>
                </div>
              </div>
            );
          })}
          <div onClick={() => fileRef.current?.click()}
            style={{ flexShrink:0, width:160, borderRadius:10, border:`2px dashed ${T.border}`,
              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
              height:138, cursor:"pointer", background:T.bg, color:T.textLight, fontSize:11, fontWeight:600, gap:6 }}>
            <span style={{ fontSize:24 }}>+</span>Add Photos
          </div>
        </div>
      )}
      {selected && (
        <div style={{ marginTop:10, background:T.primaryLight, borderRadius:10, padding:"10px 14px",
          border:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:40, height:40, borderRadius:8,
              background:`linear-gradient(135deg,${T.primaryDeep},${T.primary})`,
              display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🖼</div>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:T.text }}>{selected.kpi || "Image"}</div>
              <div style={{ fontSize:11, color:T.textLight }}>{selected.documentDate || selected.createdAt?.split('T')[0]}</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            {getUrl(selected) && <Btn variant="outline" size="sm" icon="🔍" onClick={() => window.open(getUrl(selected), '_blank')}>View Full</Btn>}
            <DeleteBtn onDelete={() => handleDelete(selected.id)} label=""/>
            <Btn variant="ghost" size="sm" onClick={() => setSelected(null)}>✕</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   DOCUMENTS SECTION — loads EVIDENCE-type entity documents from API
═══════════════════════════════════════════════════════════════════ */
const fileIcon = type => ({ PDF:"🗎",JPG:"🖼️",PNG:"🖼️",XLSX:"📊",KML:"📍",GPX:"🗺️" }[type]||"📎");
const fileColor = type => ({ PDF:T.danger,JPG:T.success,PNG:T.success,XLSX:"#2d7a4a",KML:T.primary,GPX:T.primary }[type]||T.textLight);
const extFromMime = (mime="") => { const m={"application/pdf":"PDF","image/jpeg":"JPG","image/png":"PNG","application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":"XLSX"}; return m[mime]||mime.split('/').pop().toUpperCase()||"FILE"; };
const fmtBytes = b => b>1048576?`${(b/1048576).toFixed(1)} MB`:`${(b/1024).toFixed(0)} KB`;

const DocumentsSection = ({ entityType, entityId }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [kpi, setKpi] = useState('');
  const [docDate, setDocDate] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileRef = useRef();

  const loadDocs = async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    const all = await API.getEntityDocuments(entityType, entityId);
    setDocs((all || []).filter(d => d.documentType === 'EVIDENCE'));
    setLoading(false);
  };

  useEffect(() => { loadDocs(); }, [entityType, entityId]);

  const handleFileChange = e => { setSelectedFiles(Array.from(e.target.files || [])); };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setUploading(true);
    for (const file of selectedFiles) {
      await API.uploadEntityDocument(file, { documentType:'EVIDENCE', entityType, entityId, kpi:kpi||undefined, documentDate:docDate||undefined });
    }
    setUploading(false);
    setShowUploadForm(false);
    setSelectedFiles([]); setKpi(''); setDocDate('');
    if (fileRef.current) fileRef.current.value = '';
    loadDocs();
  };

  const handleDelete = async (id) => { await API.deleteEntityDocument(id); loadDocs(); };
  const getFileUrl  = doc => doc?.file?.url || doc?.file?.signedUrl || null;
  const getFileType = doc => extFromMime(doc?.file?.mimeType || '') || 'FILE';
  const getFileName = doc => doc?.file?.originalName || doc?.file?.filename || `Document ${doc.id}`;
  const getFileSize = doc => doc?.file?.size ? fmtBytes(doc.file.size) : '—';

  return (
    <div style={{ marginTop:28 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
        marginBottom:14, paddingTop:20, borderTop:`2px solid ${T.borderLight}` }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:T.text }}>📎 Documents & Evidence</div>
          <div style={{ fontSize:12, color:T.textLight, marginTop:2 }}>
            {loading ? "Loading…" : `${docs.length} document${docs.length!==1?"s":""} attached`}
          </div>
        </div>
        <Btn variant="outline" size="sm" icon="⬆️" onClick={() => setShowUploadForm(v => !v)}>Upload Documents</Btn>
      </div>
      {showUploadForm && (
        <div style={{ background:T.primaryLight, borderRadius:10, padding:"14px 16px", marginBottom:14, border:`1px solid ${T.border}` }}>
          <div style={{ fontSize:12, fontWeight:700, color:T.primaryDark, marginBottom:10 }}>Upload Evidence Documents</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:T.textMid, display:"block", marginBottom:4 }}>Files * <span style={{ fontWeight:400, color:T.textLight }}>(select multiple)</span></label>
              <input type="file" ref={fileRef} multiple onChange={handleFileChange} style={{ ...inp, fontSize:12 }}/>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:T.textMid, display:"block", marginBottom:4 }}>KPI Label</label>
              <input value={kpi} onChange={e => setKpi(e.target.value)} placeholder="e.g. SOC%, Legal, GPS" style={{ ...inp, fontSize:13 }}/>
            </div>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:T.textMid, display:"block", marginBottom:4 }}>Document Date</label>
              <input type="date" value={docDate} onChange={e => setDocDate(e.target.value)} style={{ ...inp, fontSize:13 }}/>
            </div>
          </div>
          {selectedFiles.length > 0 && (
            <div style={{ background:"rgba(255,255,255,0.6)", borderRadius:8, padding:"8px 10px", marginBottom:10, display:"flex", flexWrap:"wrap", gap:6 }}>
              {selectedFiles.map((f, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:5, background:T.card, borderRadius:6, padding:"3px 9px", border:`1px solid ${T.borderLight}`, fontSize:11 }}>
                  <span>📄</span>
                  <span style={{ maxWidth:140, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:T.text, fontWeight:500 }}>{f.name}</span>
                  <span style={{ color:T.textLight }}>({fmtBytes(f.size)})</span>
                  <button onClick={() => setSelectedFiles(prev => prev.filter((_,j)=>j!==i))} style={{ background:"none", border:"none", cursor:"pointer", color:T.textLight, fontSize:12, padding:0, lineHeight:1 }}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn variant="ghost" size="sm" onClick={() => { setShowUploadForm(false); setSelectedFiles([]); }}>Cancel</Btn>
            <Btn variant="success" size="sm" onClick={handleUpload} disabled={selectedFiles.length === 0 || uploading} icon={uploading ? undefined : "⬆️"}>
              {uploading ? <><Spinner size={12}/>Uploading {selectedFiles.length} file{selectedFiles.length!==1?"s":""}…</> : `Upload ${selectedFiles.length > 1 ? `${selectedFiles.length} Files` : "File"}`}
            </Btn>
          </div>
        </div>
      )}
      {loading ? <div style={{ padding:20 }}><Spinner/></div> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px,1fr))", gap:10 }}>
          {docs.map(d=>{
            const ext = getFileType(d);
            const url = getFileUrl(d);
            return (
              <div key={d.id} style={{ background:T.card, borderRadius:10, border:`1px solid ${T.borderLight}`, padding:"12px 14px", display:"flex", gap:12, alignItems:"flex-start", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
                <div style={{ width:38, height:38, borderRadius:8, background:`${fileColor(ext)}14`, border:`1px solid ${fileColor(ext)}30`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{fileIcon(ext)}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{getFileName(d)}</div>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:4 }}>
                    <Badge color={ext==="PDF"?"red":ext==="XLSX"?"green":ext==="PNG"||ext==="JPG"?"blue":"gray"}>{ext}</Badge>
                    {d.kpi&&<Badge color="blue">{d.kpi}</Badge>}
                  </div>
                  <div style={{ fontSize:10, color:T.textLight }}>{getFileSize(d)} · {d.documentDate||d.createdAt?.split('T')[0]}</div>
                </div>
                <div style={{ display:"flex", gap:2, flexShrink:0, flexDirection:"column" }}>
                  {url&&<button onClick={()=>window.open(url,'_blank')} style={{ background:"none",border:"none",cursor:"pointer",fontSize:14,color:T.primary,padding:"2px 4px" }} title="View">👁</button>}
                  <button onClick={()=>handleDelete(d.id)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:14,color:T.textLight,padding:"2px 4px" }} title="Delete">🗑</button>
                </div>
              </div>
            );
          })}
          {docs.length===0&&!loading&&(
            <div style={{ background:T.bg, borderRadius:10, border:`2px dashed ${T.border}`, padding:"22px 14px", display:"flex", flexDirection:"column", alignItems:"center", cursor:"pointer", color:T.textLight, fontSize:12, fontWeight:600, gap:5 }} onClick={()=>setShowUploadForm(true)}>
              <span style={{ fontSize:22 }}>+</span>Upload First Document
            </div>
          )}
        </div>
      )}
    </div>
  );
};



/* ═══════════════════════════════════════════════════════════════════
   ENTITY MEDIA MODAL
   ─────────────────────────────────────────────────────────────────
   Used for table-based tabs (soil samples, carbon stock, harvest)
   where inline expansion is awkward. Triggered by a 📷 icon in
   the DataTable actions column.
   
   Usage:
     const [mediaModal, setMediaModal] = useState(null);
     // open:
     setMediaModal({ entityType, entityId, title: "SS-001" })
     // render:
     <EntityMediaModal modal={mediaModal} onClose={() => setMediaModal(null)} />
═══════════════════════════════════════════════════════════════════ */
const EntityMediaModal = ({ modal, onClose }) => {
  if (!modal) return null;
  return (
    <Modal
      open={!!modal}
      onClose={onClose}
      title={`📷 Photos & Documents — ${modal.title || ""}`}
      subtitle={`${modal.entityType} · ID ${modal.entityId}`}
      width={720}
    >
      <ImageGallery entityType={modal.entityType} entityId={modal.entityId} />
      <DocumentsSection entityType={modal.entityType} entityId={modal.entityId} />
    </Modal>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   ENTITY CARD  (shared by Project, Plot, Stratum)
═══════════════════════════════════════════════════════════════════ */
const THUMB_GRADS = [
  ["#1e4d61","#3F88A5"],["#2D9D5E","#52c988"],["#E8A020","#f5c05a"],["#7c4dbc","#a77de0"],
];
const EntityCard = ({ onClick, onDelete, onEdit, color="#1e4d61", icon, topLeft, topRight, subtitle, title, metrics, highlight, footer, imgCount=0 }) => (
  <div style={{ background:T.card, borderRadius:14, border:`1px solid ${T.borderLight}`, boxShadow:"0 1px 10px rgba(63,136,165,0.07)", overflow:"hidden", cursor:"pointer", transition:"transform 0.17s, box-shadow 0.17s" }}
    onMouseEnter={e=>{ e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 8px 26px rgba(63,136,165,0.16)"; }}
    onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 1px 10px rgba(63,136,165,0.07)"; }}>
    <div onClick={onClick}>
      <div style={{ height:120, background:`linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`, position:"relative", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
        <div style={{ fontSize:44, opacity:0.22, userSelect:"none" }}>{icon}</div>
        <div style={{ position:"absolute", bottom:0, left:0, right:0, height:26, background:"rgba(0,0,0,0.35)", display:"flex", alignItems:"center", gap:2, padding:"0 8px" }}>
          {THUMB_GRADS.slice(0,4).map((g,i)=>(
            <div key={i} style={{ width:20, height:20, borderRadius:3, background:`linear-gradient(135deg,${g[0]},${g[1]})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, opacity:0.9 }}>🌿</div>
          ))}
          {imgCount>0&&<span style={{ fontSize:9, color:"rgba(255,255,255,0.7)", marginLeft:4, fontWeight:600 }}>+{imgCount} photos</span>}
          {imgCount===0&&<span style={{ fontSize:9, color:"rgba(255,255,255,0.5)", marginLeft:4 }}>No photos yet</span>}
        </div>
        {topLeft&&<div style={{ position:"absolute", top:10, left:12 }}>{topLeft}</div>}
        {topRight&&<div style={{ position:"absolute", top:10, right:12 }}>{topRight}</div>}
        <div style={{ position:"absolute", bottom:34, left:14, color:"#fff" }}>
          <div style={{ fontSize:10, opacity:0.65, fontWeight:600, letterSpacing:0.5 }}>{subtitle}</div>
          <div style={{ fontSize:15, fontWeight:800, fontFamily:"Syne, sans-serif", lineHeight:1.2, maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{title}</div>
        </div>
      </div>
      <div style={{ padding:"12px 15px" }}>
        {metrics&&(
          <div style={{ display:"grid", gridTemplateColumns:`repeat(${metrics.length},1fr)`, gap:7, marginBottom:11 }}>
            {metrics.map((m,i)=>(
              <div key={i} style={{ background:T.bg, borderRadius:7, padding:"6px 8px" }}>
                <div style={{ fontSize:9, color:T.textLight, fontWeight:600, textTransform:"uppercase", letterSpacing:0.4 }}>{m.l}</div>
                <div style={{ fontSize:12, fontWeight:700, color:T.text, marginTop:1 }}>{m.v}</div>
              </div>
            ))}
          </div>
        )}
        {highlight&&(
          <div style={{ background:T.successLight, borderRadius:8, padding:"7px 11px", display:"flex", alignItems:"center", justifyContent:"space-between", border:`1px solid #b8e8cc`, marginBottom:10 }}>
            <div style={{ fontSize:11, color:T.success, fontWeight:600 }}>{highlight.label}</div>
            <div style={{ fontSize:16, fontWeight:800, color:T.success }}>{highlight.value}</div>
          </div>
        )}
        {footer}
      </div>
    </div>
    {(onEdit||onDelete)&&(
      <div style={{ padding:"0 15px 11px", borderTop:`1px solid ${T.borderLight}`, paddingTop:8, display:"flex", gap:6, justifyContent:"flex-end" }} onClick={e=>e.stopPropagation()}>
        {onEdit&&(
          <button onClick={onEdit} style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 11px", borderRadius:7, border:`1px solid ${T.border}`, background:"#fff", cursor:"pointer", fontSize:12, color:T.primaryDark, fontWeight:600, fontFamily:"inherit" }}>
            ✏️ Edit
          </button>
        )}
        {onDelete&&<DeleteBtn onDelete={onDelete} label="Delete"/>}
      </div>
    )}
  </div>
);

/* ═══════════════════════════════════════════════════════════════════
   NAVIGATION
═══════════════════════════════════════════════════════════════════ */
const TOP_TABS = [
  { id:"projects",  label:"Projects",  icon:"📁" },
  { id:"plots",     label:"Plots",     icon:"🗺️" },
  { id:"strata",    label:"Strata",    icon:"🧱" },
];

const SubTabs = ({ tabs, active, onChange }) => (
  <div style={{ overflowX:"auto", marginBottom:20 }}>
    <div style={{ display:"flex", borderBottom:`2px solid ${T.borderLight}`, width:"max-content", minWidth:"100%" }}>
      {tabs.map(tab=>(
        <button key={tab.id} onClick={()=>onChange(tab.id)} style={{ display:"flex", alignItems:"center", gap:5, padding:"9px 15px", border:"none", borderBottom:`2px solid ${active===tab.id?T.primary:"transparent"}`, marginBottom:-2, background:"transparent", cursor:"pointer", fontFamily:"inherit", fontSize:13, whiteSpace:"nowrap", transition:"all 0.15s", fontWeight:active===tab.id?700:500, color:active===tab.id?T.primary:T.textLight }}>
          <span>{tab.icon}</span>{tab.label}
        </button>
      ))}
    </div>
  </div>
);

const PLOT_TABS = [
  { id:"overview",label:"Overview",icon:"📋" },
  { id:"trees",label:"Trees",icon:"🌲" },{ id:"strata",label:"Strata",icon:"🧱" },
  { id:"soil",label:"Soil Samples",icon:"⚗️" },{ id:"carbon",label:"Carbon Stock",icon:"📊" },
  { id:"management",label:"Management",icon:"🔧" },{ id:"harvest",label:"Harvest",icon:"🌾" },
  { id:"monitoring",label:"Monitoring",icon:"📅" },{ id:"leakage",label:"Emissions",icon:"💨" },
  { id:"biodiversity",label:"Biodiversity",icon:"🦋" },{ id:"stratumqc",label:"Stratum QC",icon:"✅" },
];

/* ═══════════════════════════════════════════════════════════════════
   COLOR HELPERS
═══════════════════════════════════════════════════════════════════ */
const luColor = lu => ({ FOREST:T.success, AGRICULTURE:T.warning, GRASSLAND:"#7c4dbc" }[lu]||T.primary);
const STRATA_COLORS = ["#1e4d61","#2d6b84","#2D9D5E","#7c4dbc","#E8A020","#D94F41"];

/* ═══════════════════════════════════════════════════════════════════
   DASHBOARD VIEW
═══════════════════════════════════════════════════════════════════ */
export const DashboardView = ({ onNavigate }) => {
  const { data:projects, loading } = useApiData(() => API.getProjects());
  const { data:plots } = useApiData(() => API.getPlots());
  const { data:strata } = useApiData(() => API.getStrata());

  const totalCredits = projects.reduce((s,p)=>s+(Number(p.carbonCredits)||0),0);
  const totalTrees   = plots.reduce((s,p)=>s+(Number(p.trees)||0),0);
  const totalArea    = plots.reduce((s,p)=>s+(Number(p.areaHectares)||0),0);

  const projectComparison = projects.slice(0,6).map(p=>({ name:(p.officialProjectName||p.projectId||'').split(' ')[0], credits: Number(p.carbonCredits)||0 }));

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:21, fontWeight:800, color:T.text, fontFamily:"Syne, sans-serif" }}>Carbon Sequestration Dashboard</h2>
        <p style={{ margin:"3px 0 0", color:T.textLight, fontSize:13 }}>Platform-wide summary · Monitoring Period: Jan 2024 – Dec 2024</p>
      </div>
      {loading ? <LoadingOverlay/> : (
        <>
          <div style={{ display:"flex", gap:11, flexWrap:"wrap", marginBottom:11 }}>
            <StatCard icon="✦" label="Total Carbon Credits" value={totalCredits.toLocaleString()} color={T.success}/>
            <StatCard icon="📁" label="Active Projects" value={projects.length} color={T.primary}/>
            <StatCard icon="🗺️" label="Total Plots" value={plots.length} sub={`${totalArea.toFixed(0)} ha total`} color={T.primary}/>
            <StatCard icon="🌲" label="Total Trees" value={totalTrees.toLocaleString()} color={T.success}/>
            <StatCard icon="🧱" label="Total Strata" value={strata.length} color="#7c4dbc"/>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:14, marginBottom:14 }}>
            <ChartCard title="Carbon Stock Trend" subtitle="Estimated accumulation (tC/ha)" height={210}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={carbonTrendData}>
                  <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={T.primary} stopOpacity={0.28}/><stop offset="95%" stopColor={T.primary} stopOpacity={0}/></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.borderLight}/>
                  <XAxis dataKey="year" tick={{ fontSize:11, fill:T.textLight }}/><YAxis tick={{ fontSize:11, fill:T.textLight }}/>
                  <Tooltip contentStyle={{ borderRadius:8, border:`1px solid ${T.border}`, fontSize:12 }}/>
                  <Area type="monotone" dataKey="carbon" name="Carbon (tC/ha)" stroke={T.primary} fill="url(#cg)" strokeWidth={2.5} dot={{ r:3 }}/>
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Biomass Pools" subtitle="Distribution (tC/ha)" height={210}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={biomassBreakdown} cx="50%" cy="50%" outerRadius={65} dataKey="value" label={({percent})=>`${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={10}>{biomassBreakdown.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}</Pie><Tooltip contentStyle={{ fontSize:12, borderRadius:8 }}/><Legend layout="horizontal" verticalAlign="bottom" align="center" iconType="circle" iconSize={6} wrapperStyle={{ fontSize: 9, lineHeight: "12px", paddingTop: 0, marginTop: -8 }} formatter={(value) => (<span style={{ fontSize: 9 }}>{value}</span>)} /></PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:24 }}>
            <ChartCard title="Project Comparison" subtitle="Carbon Credits" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={projectComparison} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={T.borderLight}/>
                  <XAxis type="number" tick={{ fontSize:10 }}/><YAxis dataKey="name" type="category" tick={{ fontSize:10 }} width={60}/>
                  <Tooltip contentStyle={{ fontSize:12, borderRadius:8 }}/><Bar dataKey="credits" name="Credits" fill={T.primary} radius={[0,4,4,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Tree Health" subtitle="All plots" height={200}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={treeHealthData} cx="50%" cy="50%" innerRadius={46} outerRadius={72} dataKey="value" label={({name,value})=>`${name} ${value}%`} labelLine={false} fontSize={10}><Cell fill={T.success}/><Cell fill={T.warning}/><Cell fill={T.danger}/></Pie><Tooltip contentStyle={{ fontSize:12, borderRadius:8 }}/></PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   PROJECTS VIEW
═══════════════════════════════════════════════════════════════════ */
const ProjectsView = ({ onNavigate, hideHeader }) => {
  const { data:projects, loading, refresh } = useApiData(()=>API.getProjects());
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const handleDelete = async (id) => { await API.deleteProject(id); refresh(); };
  const openEdit = (p) => { setEditItem(p); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditItem(null); };

  return (
    <div>
      <Modal open={showForm} onClose={closeForm}
        title={editItem?"Edit Project":"New Farm Project"}
        subtitle={editItem?"Update project details":"Add a new carbon project to the platform"}
        width={700}>
        <ProjectForm onClose={closeForm} onRefresh={refresh} initialData={editItem}/>
      </Modal>
      {!hideHeader && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div><h2 style={{ margin:0, fontSize:21, fontWeight:800, color:T.text, fontFamily:"Syne, sans-serif" }}>Farm Projects</h2><p style={{ margin:"3px 0 0", color:T.textLight, fontSize:13 }}>{projects.length} project{projects.length!==1?"s":""} registered</p></div>
          <Btn icon="+" onClick={()=>setShowForm(true)}>New Project</Btn>
        </div>
      )}
      {loading ? <LoadingOverlay/> : projects.length===0 ? <EmptyState icon="📁" message="No projects yet" sub="Create your first carbon project to get started." action={<Btn icon="+" onClick={()=>setShowForm(true)}>New Project</Btn>}/> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(330px,1fr))", gap:16 }}>
          {projects.map((p,idx)=>(
            <EntityCard key={p.id}
              onClick={()=>onNavigate("project-detail",p)}
              onDelete={()=>handleDelete(p.id)}
              onEdit={()=>openEdit(p)}
              color={STRATA_COLORS[idx%STRATA_COLORS.length]}
              icon="🌿"
              topLeft={p.projectType&&<Badge color="gray">{p.projectType}</Badge>}
              topRight={p.status&&<Badge color={p.status==="active"?"green":p.status==="monitoring"?"blue":"yellow"}>{p.status.toUpperCase()}</Badge>}
              subtitle={p.projectId}
              title={p.officialProjectName||p.projectId}
              metrics={[{ l:"Area", v:p.totalAreaHectares?`${Number(p.totalAreaHectares).toLocaleString()} ha`:"—" },{ l:"Climate", v:p.climateZone||"—" },{ l:"Manager", v:p.projectManager||"—" }]}
              highlight={p.carbonCredits?{ label:"✦ Carbon Credits", value:Number(p.carbonCredits).toLocaleString() }:null}
              footer={<div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.textLight }}><span>📅 {p.projectStartDate||"—"}</span><span>📍 {p.climateZone||"—"}</span></div>}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   ALL PLOTS VIEW
═══════════════════════════════════════════════════════════════════ */
const AllPlotsView = ({ onNavigate, hideHeader }) => {
  const { data:plots, loading, refresh } = useApiData(()=>API.getPlots());
  const { data:projects } = useApiData(()=>API.getProjects());
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const handleDelete = async (id) => { await API.deletePlot(id); refresh(); };
  const openEdit = (p) => { setEditItem(p); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditItem(null); };

  return (
    <div>
      <Modal open={showForm} onClose={closeForm}
        title={editItem?"Edit Plot":"New Plot"}
        subtitle={editItem?"Update plot details":"Add a new farm plot to a project"}
        width={680}>
        <PlotForm onClose={closeForm} onRefresh={refresh} projects={projects} initialData={editItem}/>
      </Modal>
      {!hideHeader && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div><h2 style={{ margin:0, fontSize:21, fontWeight:800, color:T.text, fontFamily:"Syne, sans-serif" }}>All Plots</h2><p style={{ margin:"3px 0 0", color:T.textLight, fontSize:13 }}>{plots.length} plot{plots.length!==1?"s":""} across all projects</p></div>
          <Btn icon="+" onClick={()=>setShowForm(true)}>New Plot</Btn>
        </div>
      )}
      {loading ? <LoadingOverlay/> : plots.length===0 ? <EmptyState icon="🗺️" message="No plots yet" sub="Add your first plot to a project." action={<Btn icon="+" onClick={()=>setShowForm(true)}>New Plot</Btn>}/> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(330px,1fr))", gap:16 }}>
          {plots.map(p=>(
            <EntityCard key={p.id}
              onClick={()=>onNavigate("plot-detail",p)}
              onDelete={()=>handleDelete(p.id)}
              onEdit={()=>openEdit(p)}
              color={luColor(p.landUseType)}
              icon="🗺️"
              topLeft={p.landUseType&&<Badge color={p.landUseType==="FOREST"?"green":p.landUseType==="AGRICULTURE"?"yellow":"blue"}>{p.landUseType}</Badge>}
              topRight={p.status&&<Badge color={p.status==="active"?"green":"blue"}>{p.status?.toUpperCase()}</Badge>}
              subtitle={`${p.plotId} · ${p.project?.officialProjectName||`Project #${p.projectId}`}`}
              title={`${p.vegetationType||p.landUseType||'Plot'}`}
              metrics={[{ l:"Area", v:p.areaHectares?`${p.areaHectares} ha`:"—" },{ l:"Prev Use", v:p.previousLandUse||"—" },{ l:"Conv Year", v:p.yearOfConversion||"—" }]}
              highlight={p.speciesComposition?{ label:"🌱 Species", value:p.speciesComposition.split(',')[0] }:null}
              footer={<div style={{ fontSize:11, color:T.textLight }}>🌿 {p.speciesComposition||"—"}</div>}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   ALL STRATA VIEW
═══════════════════════════════════════════════════════════════════ */
const AllStrataView = ({ onNavigate, hideHeader }) => {
  const { data:strata, loading, refresh } = useApiData(()=>API.getStrata());
  const { data:projects } = useApiData(()=>API.getProjects());
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const handleDelete = async (id) => { await API.deleteStratum(id); refresh(); };
  const openEdit = (s) => { setEditItem(s); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditItem(null); };

  return (
    <div>
      <Modal open={showForm} onClose={closeForm}
        title={editItem?"Edit Stratum":"New Stratum"}
        subtitle={editItem?"Update stratum details":"Define a new stratum for a project"}
        width={640}>
        <StratumForm onClose={closeForm} onRefresh={refresh} projects={projects} initialData={editItem}/>
      </Modal>
      {!hideHeader && (
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
          <div><h2 style={{ margin:0, fontSize:21, fontWeight:800, color:T.text, fontFamily:"Syne, sans-serif" }}>All Strata</h2><p style={{ margin:"3px 0 0", color:T.textLight, fontSize:13 }}>{strata.length} strat{strata.length!==1?"a":"um"} across all projects</p></div>
          <Btn icon="+" onClick={()=>setShowForm(true)}>New Stratum</Btn>
        </div>
      )}
      {loading ? <LoadingOverlay/> : strata.length===0 ? <EmptyState icon="🧱" message="No strata yet" sub="Define your first stratum for a project." action={<Btn icon="+" onClick={()=>setShowForm(true)}>New Stratum</Btn>}/> : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(330px,1fr))", gap:16 }}>
          {strata.map((s,idx)=>(
            <EntityCard key={s.id}
              onClick={()=>onNavigate("stratum-detail",s)}
              onDelete={()=>handleDelete(s.id)}
              onEdit={()=>openEdit(s)}
              color={STRATA_COLORS[idx%STRATA_COLORS.length]}
              icon="🧱"
              topLeft={<Badge color="blue">STRATUM</Badge>}
              topRight={<Badge color={s.qcStatus==="passed"?"green":"yellow"}>{s.qcStatus?`QC ${s.qcStatus.toUpperCase()}`:"QC PENDING"}</Badge>}
              subtitle={s.project?.officialProjectName||`Project #${s.projectId}`}
              title={s.stratumName}
              metrics={[{ l:"Area", v:s.areaHectares?`${s.areaHectares} ha`:"—" },{ l:"Avg Age", v:s.averageTreeAgeYears?`${s.averageTreeAgeYears} yr`:"—" },{ l:"Soil", v:s.predominantSoilType||"—" }]}
              highlight={null}
              footer={<div style={{ fontSize:11, color:T.textLight }}>🌿 {s.dominantSpecies||"—"}</div>}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   PROJECT DETAIL
═══════════════════════════════════════════════════════════════════ */
const ProjectDetailView = ({ project, onNavigate }) => {
  const [tab, setTab] = useState("overview");
  const { data:projects } = useApiData(()=>API.getProjects());
  const { data:plots, loading:lp, refresh:rp } = useApiData(()=>API.getPlots(project.id),[project.id]);
  const { data:strata, loading:ls, refresh:rs } = useApiData(()=>API.getStrata(project.id),[project.id]);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState("plot");
  const [editItem, setEditItem] = useState(null);
  const [showProjectEdit, setShowProjectEdit] = useState(false);

  const handleDeletePlot = async (id) => { await API.deletePlot(id); rp(); };
  const handleDeleteStratum = async (id) => { await API.deleteStratum(id); rs(); };

  const openChildEdit = (type, item) => { setFormType(type); setEditItem(item); setShowForm(true); };
  const closeChildForm = () => { setShowForm(false); setEditItem(null); };

  return (
    <div>
      <Modal open={showProjectEdit} onClose={()=>setShowProjectEdit(false)} title="Edit Project" subtitle="Update project details" width={700}>
        <ProjectForm onClose={()=>setShowProjectEdit(false)} onRefresh={()=>{}} initialData={project}/>
      </Modal>
      <Modal open={showForm} onClose={closeChildForm}
        title={editItem ? `Edit ${formType==="plot"?"Plot":"Stratum"}` : (formType==="plot"?"New Plot":"New Stratum")}
        subtitle={editItem ? "Update details" : (formType==="plot"?"Add a plot to this project":"Define a stratum for this project")}
        width={680}>
        {formType==="plot"
          ? <PlotForm onClose={closeChildForm} onRefresh={rp} projects={projects} preselectedProjectId={project.id} initialData={editItem}/>
          : <StratumForm onClose={closeChildForm} onRefresh={rs} projects={projects} preselectedProjectId={project.id} initialData={editItem}/>}
      </Modal>

      <Breadcrumb crumbs={[{ label:"Projects", onClick:()=>onNavigate("projects") },{ label:project.officialProjectName||project.projectId }]}/>
      <div style={{ background:`linear-gradient(135deg, ${T.primaryDeep} 0%, ${T.primary} 100%)`, borderRadius:14, padding:"20px 26px", marginBottom:20, color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div>
          <div style={{ fontSize:10, opacity:0.62, fontWeight:600, letterSpacing:0.8, marginBottom:4 }}>{project.projectId} · {project.projectType||"—"}</div>
          <h2 style={{ margin:"0 0 6px", fontSize:20, fontWeight:800, fontFamily:"Syne, sans-serif" }}>{project.officialProjectName||project.projectId}</h2>
          <div style={{ display:"flex", gap:16, fontSize:12, opacity:0.82 }}>
            <span>📍 {project.climateZone||"—"}</span>
            <span>👤 {project.projectManager||"—"}</span>
            <span>📅 {project.projectStartDate||"—"}</span>
          </div>
        </div>
        <div style={{ textAlign:"right", flexShrink:0, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8 }}>
          <button onClick={()=>setShowProjectEdit(true)} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 13px", borderRadius:8, border:"1px solid rgba(255,255,255,0.4)", background:"rgba(255,255,255,0.15)", cursor:"pointer", color:"#fff", fontSize:12, fontWeight:600, fontFamily:"inherit", backdropFilter:"blur(4px)" }}>
            ✏️ Edit Project
          </button>
          {project.carbonCredits&&<><div style={{ fontSize:10, opacity:0.62, fontWeight:600 }}>CARBON CREDITS</div><div style={{ fontSize:28, fontWeight:800, lineHeight:1.1 }}>{Number(project.carbonCredits).toLocaleString()}</div></>}
          <Badge color="green">{project.status?.toUpperCase()||"ACTIVE"}</Badge>
        </div>
      </div>

      <SubTabs tabs={[{ id:"overview",label:"Overview",icon:"📋" },{ id:"plots",label:`Plots (${plots.length})`,icon:"🗺️" },{ id:"strata",label:`Strata (${strata.length})`,icon:"🧱" }]} active={tab} onChange={setTab}/>

      {tab==="overview"&&(
        <div>
          {/* Project-level images/docs — keyed to project.id, which is correct */}
          <ImageGallery entityType={ENTITY_TYPES.project} entityId={project.id}/>
          <div style={{ display:"flex", gap:11, flexWrap:"wrap", marginBottom:18 }}>
            <StatCard icon="🗺️" label="Total Area" value={project.totalAreaHectares?`${project.totalAreaHectares} ha`:"—"} color={T.primary}/>
            <StatCard icon="📊" label="Plots" value={plots.length} color={T.primary}/>
            <StatCard icon="🧱" label="Strata" value={strata.length} color={T.primary}/>
          </div>
          <div style={{ marginBottom:18 }}>
            <MapCard title="Project Boundaries" subtitle="GPS polygon boundary (SRID 4326)" label={project.officialProjectName||"Project"} boundary={project.gpsBoundary} centroid={project.centroid} height={280}/>
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:9 }}>Project Details</div>
          <InfoGrid items={[
            { label:"Project ID", value:project.projectId },{ label:"Type", value:project.projectType },
            { label:"Climate Zone", value:project.climateZone },{ label:"Baseline Year", value:project.baselineYear },
            { label:"Start Date", value:project.projectStartDate },{ label:"Monitoring End", value:project.monitoringPeriodEnd },
            { label:"Avg Rainfall", value:project.averageAnnualRainfallMm?`${project.averageAnnualRainfallMm} mm/yr`:"—" },
            { label:"Avg Temp", value:project.averageTemperatureCelsius?`${project.averageTemperatureCelsius}°C`:"—" },
            { label:"Soil Type", value:project.soilType },{ label:"Elevation", value:project.elevationMeters?`${project.elevationMeters} m`:"—" },
            { label:"Manager", value:project.projectManager },{ label:"Contact", value:project.contactInformation },
          ]}/>
          {project.preProjectConditionDescription&&(
            <div style={{ marginTop:12, background:T.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${T.borderLight}` }}>
              <div style={{ fontSize:10, fontWeight:700, color:T.textLight, textTransform:"uppercase", marginBottom:4 }}>Pre-Project Condition</div>
              <div style={{ fontSize:13, color:T.textMid }}>{project.preProjectConditionDescription}</div>
            </div>
          )}
          {/* Project-level documents — keyed to project.id, which is correct */}
          <DocumentsSection entityType={ENTITY_TYPES.project} entityId={project.id}/>
        </div>
      )}
      {tab==="plots"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}><Btn icon="+" onClick={()=>{ setFormType("plot"); setShowForm(true); }}>Add Plot</Btn></div>
          {lp ? <LoadingOverlay/> : plots.length===0 ? <EmptyState icon="🗺️" message="No plots in this project" action={<Btn icon="+" onClick={()=>{ setFormType("plot"); setShowForm(true); }}>Add Plot</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px,1fr))", gap:14 }}>
              {plots.map(p=>(
                <EntityCard key={p.id}
                  onClick={()=>onNavigate("plot-detail",p)}
                  onDelete={()=>handleDeletePlot(p.id)}
                  onEdit={()=>openChildEdit("plot",p)}
                  color={luColor(p.landUseType)}
                  icon="🗺️"
                  topLeft={p.landUseType&&<Badge color={p.landUseType==="FOREST"?"green":p.landUseType==="AGRICULTURE"?"yellow":"blue"}>{p.landUseType}</Badge>}
                  topRight={p.status&&<Badge color={p.status==="active"?"green":"blue"}>{p.status?.toUpperCase()}</Badge>}
                  subtitle={p.plotId}
                  title={p.vegetationType||p.landUseType||"Plot"}
                  metrics={[{ l:"Area", v:p.areaHectares?`${p.areaHectares} ha`:"—" },{ l:"Prev Use", v:p.previousLandUse||"—" }]}
                  highlight={null}
                  footer={<div style={{ fontSize:11, color:T.textLight }}>🌱 {p.speciesComposition||"—"}</div>}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {tab==="strata"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}><Btn icon="+" onClick={()=>{ setFormType("stratum"); setShowForm(true); }}>Add Stratum</Btn></div>
          {ls ? <LoadingOverlay/> : strata.length===0 ? <EmptyState icon="🧱" message="No strata in this project" action={<Btn icon="+" onClick={()=>{ setFormType("stratum"); setShowForm(true); }}>Add Stratum</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(300px,1fr))", gap:14 }}>
              {strata.map((s,idx)=>(
                <EntityCard key={s.id}
                  onClick={()=>onNavigate("stratum-detail",s)}
                  onDelete={()=>handleDeleteStratum(s.id)}
                  onEdit={()=>openChildEdit("stratum",s)}
                  color={STRATA_COLORS[idx%STRATA_COLORS.length]}
                  icon="🧱"
                  topLeft={<Badge color={s.qcStatus==="passed"?"green":"yellow"}>QC {s.qcStatus?.toUpperCase()||"PENDING"}</Badge>}
                  topRight={null}
                  subtitle={`${plots.length} plots`}
                  title={s.stratumName}
                  metrics={[{ l:"Area", v:s.areaHectares?`${s.areaHectares} ha`:"—" },{ l:"Avg Age", v:s.averageTreeAgeYears?`${s.averageTreeAgeYears} yr`:"—" }]}
                  highlight={null}
                  footer={<div style={{ fontSize:11, color:T.textLight }}>🌿 {s.dominantSpecies||"—"}</div>}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   PLOT DETAIL
═══════════════════════════════════════════════════════════════════ */
const PlotDetailView = ({ plot, onNavigate }) => {
  const [tab, setTab] = useState("overview");
  const [modal, setModal] = useState(null);
  const [showPlotEdit, setShowPlotEdit] = useState(false);

  // ─── Media modal for table-based tabs (soil, carbon, harvest) ───
  // shape: { entityType, entityId, title } | null
  const [mediaModal, setMediaModal] = useState(null);

  const { data:projects } = useApiData(()=>API.getProjects());

  const { data:trees,      loading:lt, refresh:rt }  = useApiData(()=>API.getTreesByPlot(plot.id),[plot.id,tab==="trees"]);
  const { data:soilSamples,loading:lss,refresh:rss } = useApiData(()=>API.getSoilSamplesByPlot(plot.id),[plot.id,tab==="soil"]);
  const { data:carbonCalcs,loading:lcc,refresh:rcc } = useApiData(()=>API.getCarbonStockByPlot(plot.id),[plot.id,tab==="carbon"]);
  const { data:activities, loading:lma,refresh:rma } = useApiData(()=>API.getManagementActivitiesByPlot(plot.id),[plot.id,tab==="management"]);
  const { data:harvests,   loading:lh, refresh:rh }  = useApiData(()=>API.getHarvestRecordsByPlot(plot.id),[plot.id,tab==="harvest"]);
  const { data:schedules,  loading:lm, refresh:rm }  = useApiData(()=>API.getMonitoringSchedulesByPlot(plot.id),[plot.id,tab==="monitoring"]);
  const { data:leakages,   loading:ll, refresh:rl }  = useApiData(()=>API.getEmissionLeakagesByPlot(plot.id),[plot.id,tab==="leakage"]);
  const { data:bioObs,     loading:lb, refresh:rb }  = useApiData(()=>API.getBiodiversityByPlot(plot.id),[plot.id,tab==="biodiversity"]);
  const { data:strata }                               = useApiData(()=>API.getStrata(plot.projectId),[plot.projectId]);

  const firstStratumId = strata[0]?.id;
  const { data:qcList,  loading:lq, refresh:rq }    = useApiData(()=>firstStratumId?API.getStratumQCByStratum(firstStratumId):Promise.resolve([]),[firstStratumId,tab==="stratumqc"]);

  const deleteAndRefresh = (apiFn, refreshFn) => async (id) => { await apiFn(id); refreshFn(); };

  const refreshMap = { tree:rt, soil:rss, carbon:rcc, mgmt:rma, harvest:rh, monitoring:rm, leakage:rl, bio:rb, qc:rq };

  const openAdd  = type => setModal({ type, item:null });
  const openEdit = (type, item) => setModal({ type, item });
  const closeModal = () => setModal(null);

  const MODAL_TITLES = {
    tree:"Tree Record", soil:"Soil Sample", carbon:"Carbon Stock Calculation",
    mgmt:"Management Activity", harvest:"Harvest Record", monitoring:"Monitoring Schedule",
    leakage:"Emission / Leakage Assessment", bio:"Biodiversity Observation", qc:"Stratum QC Assessment",
  };

  const latestCarbon = carbonCalcs[0];

  const CardActions = ({ onEdit, onDelete, onMedia }) => (
    <div style={{ display:"flex", justifyContent:"flex-end", gap:6, marginTop:8 }} onClick={e=>e.stopPropagation()}>
      {onMedia&&(
        <button onClick={onMedia} style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:6, border:`1px solid ${T.border}`, background:"#fff", cursor:"pointer", fontSize:11, color:T.primary, fontWeight:600, fontFamily:"inherit" }} title="Photos & Documents">📷 Media</button>
      )}
      <button onClick={onEdit} style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:6, border:`1px solid ${T.border}`, background:"#fff", cursor:"pointer", fontSize:11, color:T.primaryDark, fontWeight:600, fontFamily:"inherit" }}>✏️ Edit</button>
      <DeleteBtn onDelete={onDelete}/>
    </div>
  );

  return (
    <div>
      {/* Edit Plot modal */}
      <Modal open={showPlotEdit} onClose={()=>setShowPlotEdit(false)} title="Edit Plot" subtitle="Update plot details" width={680}>
        <PlotForm onClose={()=>setShowPlotEdit(false)} onRefresh={()=>{}} projects={projects} initialData={plot}/>
      </Modal>

      {/* Add / Edit entity modal */}
      {modal&&(
        <Modal open={!!modal} onClose={closeModal}
          title={`${modal.item?"Edit":"Add"} ${MODAL_TITLES[modal.type]||modal.type}`}
          width={680}>
          {modal.type==="tree"      && <TreeForm           onClose={closeModal} onRefresh={refreshMap.tree}       plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="soil"      && <SoilSampleForm     onClose={closeModal} onRefresh={refreshMap.soil}       plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="carbon"    && <CarbonStockForm    onClose={closeModal} onRefresh={refreshMap.carbon}     plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="mgmt"      && <ManagementActivityForm onClose={closeModal} onRefresh={refreshMap.mgmt}   plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="harvest"   && <HarvestForm        onClose={closeModal} onRefresh={refreshMap.harvest}    plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="monitoring"&& <MonitoringForm     onClose={closeModal} onRefresh={refreshMap.monitoring} plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="leakage"   && <EmissionLeakageForm onClose={closeModal} onRefresh={refreshMap.leakage}   plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="bio"       && <BiodiversityForm   onClose={closeModal} onRefresh={refreshMap.bio}        plotId={plot.id} initialData={modal.item}/>}
          {modal.type==="qc"        && <StratumQCForm      onClose={closeModal} onRefresh={refreshMap.qc}         strata={strata} preselectedStratumId={firstStratumId} initialData={modal.item}/>}
        </Modal>
      )}

      {/* Per-entity media modal (for table-based tabs) */}
      <EntityMediaModal modal={mediaModal} onClose={() => setMediaModal(null)} />

      <Breadcrumb crumbs={[{ label:"Projects", onClick:()=>onNavigate("projects") },{ label:plot.project?.officialProjectName||`Project #${plot.projectId}`, onClick:()=>onNavigate("project-detail",{ id:plot.projectId, officialProjectName:plot.project?.officialProjectName }) },{ label:`Plot: ${plot.plotId}` }]}/>

      {/* ── Always-visible gradient banner (mirrors Project Detail) ── */}
      <div style={{ background:`linear-gradient(135deg, ${luColor(plot.landUseType)} 0%, ${luColor(plot.landUseType)}99 100%)`, borderRadius:14, padding:"20px 26px", marginBottom:20, color:"#fff", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div>
          <div style={{ fontSize:10, opacity:0.65, fontWeight:600, letterSpacing:0.8, marginBottom:4 }}>
            {plot.plotId} · {plot.project?.officialProjectName||`Project #${plot.projectId}`}
          </div>
          <h2 style={{ margin:"0 0 6px", fontSize:20, fontWeight:800, fontFamily:"Syne, sans-serif" }}>
            {plot.vegetationType||plot.landUseType||"Plot"}
          </h2>
          <div style={{ display:"flex", gap:14, fontSize:12, opacity:0.85, flexWrap:"wrap" }}>
            {plot.landUseType&&<span>🌿 {plot.landUseType}</span>}
            {plot.areaHectares&&<span>📐 {plot.areaHectares} ha</span>}
            {plot.yearOfConversion&&<span>📅 Conv. {plot.yearOfConversion}</span>}
            {plot.plantingDensityTreesPerHa&&<span>🌲 {plot.plantingDensityTreesPerHa}/ha density</span>}
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:8, flexShrink:0 }}>
          <button onClick={()=>setShowPlotEdit(true)} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 13px", borderRadius:8, border:"1px solid rgba(255,255,255,0.4)", background:"rgba(255,255,255,0.15)", cursor:"pointer", color:"#fff", fontSize:12, fontWeight:600, fontFamily:"inherit", backdropFilter:"blur(4px)" }}>
            ✏️ Edit Plot
          </button>
          {latestCarbon&&(
            <>
              <div style={{ fontSize:10, opacity:0.65, fontWeight:600 }}>LATEST CARBON STOCK</div>
              <div style={{ fontSize:26, fontWeight:800, lineHeight:1.1 }}>{latestCarbon.totalCarbonStockTcPerHa} <span style={{ fontSize:13, opacity:0.8 }}>tC/ha</span></div>
            </>
          )}
          <div style={{ display:"flex", gap:6 }}>
            {plot.landUseType&&<Badge color={plot.landUseType==="FOREST"?"green":plot.landUseType==="AGRICULTURE"?"yellow":"blue"}>{plot.landUseType}</Badge>}
            {plot.status&&<Badge color={plot.status==="active"?"green":"blue"}>{plot.status?.toUpperCase()}</Badge>}
          </div>
        </div>
      </div>

      <SubTabs tabs={[
        { id:"overview",    label:"Overview",                    icon:"📋" },
        { id:"trees",       label:`Trees (${trees.length})`,     icon:"🌲" },
        { id:"strata",      label:`Strata (${strata.length})`,   icon:"🧱" },
        { id:"soil",        label:"Soil Samples",                icon:"⚗️" },
        { id:"carbon",      label:"Carbon Stock",                icon:"📊" },
        { id:"management",  label:"Management",                  icon:"🔧" },
        { id:"harvest",     label:"Harvest",                     icon:"🌾" },
        { id:"monitoring",  label:"Monitoring",                  icon:"📅" },
        { id:"leakage",     label:"Emissions",                   icon:"💨" },
        { id:"biodiversity",label:"Biodiversity",                icon:"🦋" },
        { id:"stratumqc",   label:"Stratum QC",                  icon:"✅" },
      ]} active={tab} onChange={setTab}/>

      {/* ─── OVERVIEW ─── */}
      {tab==="overview"&&(
        <div>
          {/* Summary stat cards */}
          <div style={{ display:"flex", gap:11, flexWrap:"wrap", marginBottom:18 }}>
            <StatCard icon="📐" label="Total Area" value={plot.areaHectares?`${plot.areaHectares} ha`:"—"} color={T.primary}/>
            <StatCard icon="🌲" label="Trees Recorded" value={trees.length} color={T.success}/>
            <StatCard icon="🧱" label="Overlapping Strata" value={strata.length} color="#7c4dbc"/>
            {latestCarbon&&<StatCard icon="📊" label="Carbon Stock" value={`${latestCarbon.totalCarbonStockTcPerHa} tC/ha`} color={T.success}/>}
          </div>

          {/* Boundary map */}
          <div style={{ marginBottom:18 }}>
            <MapCard title="Plot Boundary" subtitle="GPS polygon boundary (SRID 4326)" label={`Plot ${plot.plotId}`} boundary={plot.gpsBoundary} centroid={plot.centroid} showPoints height={280}/>
          </div>

          {/* Plot detail grid */}
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:9 }}>Plot Details</div>
          <InfoGrid items={[
            { label:"Plot ID",           value:plot.plotId },
            { label:"Land Use Type",     value:plot.landUseType },
            { label:"Previous Land Use", value:plot.previousLandUse },
            { label:"Year of Conversion",value:plot.yearOfConversion },
            { label:"Area (ha)",         value:plot.areaHectares },
            { label:"Planting Density",  value:plot.plantingDensityTreesPerHa?`${plot.plantingDensityTreesPerHa} trees/ha`:"—" },
            { label:"Vegetation Type",   value:plot.vegetationType },
            { label:"Species Composition",value:plot.speciesComposition },
          ]}/>

          {/* Plot-level images & evidence */}
          <div style={{ marginTop:20 }}>
            <ImageGallery entityType={ENTITY_TYPES.plot} entityId={plot.id}/>
            <DocumentsSection entityType={ENTITY_TYPES.plot} entityId={plot.id}/>
          </div>
        </div>
      )}

      {/* ─── TREES ─── */}
      {tab==="trees"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{trees.length} Trees Recorded</div>
            <Btn icon="+" onClick={()=>openAdd("tree")}>Add Tree</Btn>
          </div>
          {lt ? <LoadingOverlay/> : trees.length===0 ? <EmptyState icon="🌲" message="No trees recorded yet" action={<Btn icon="+" onClick={()=>openAdd("tree")}>Add First Tree</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(265px,1fr))", gap:11 }}>
              {trees.map(t=>(
                <div key={t.id} style={{ background:T.card, borderRadius:10, border:`1px solid ${T.borderLight}`, overflow:"hidden" }}>
                  <div style={{ height:46, background:`linear-gradient(135deg, ${T.primaryDeep}, ${T.primary})`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 12px" }}>
                    <div style={{ color:"#fff" }}><div style={{ fontSize:9, opacity:0.7, fontWeight:600 }}>{t.treeId}</div><div style={{ fontSize:12, fontStyle:"italic", fontWeight:700 }}>{t.speciesName}</div></div>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      {t.healthStatus&&<Badge color={t.healthStatus==="GOOD"?"green":t.healthStatus==="FAIR"?"yellow":"red"}>{t.healthStatus}</Badge>}
                    </div>
                  </div>
                  <div style={{ padding:"10px 12px" }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:5, marginBottom:8 }}>
                      {[{ l:"DBH", v:`${t.dbhCm}cm` },{ l:"Height", v:t.heightMeters?`${t.heightMeters}m`:"—" },{ l:"Age", v:t.ageYears?`${t.ageYears}yr`:"—" },{ l:"Crown", v:t.crownDiameterMeters?`${t.crownDiameterMeters}m`:"—" },{ l:"Planted", v:t.yearPlanted||"—" },{ l:"Status", v:t.lifeStatus||"—" }].map((m,i)=>(
                        <div key={i} style={{ background:T.bg, borderRadius:5, padding:"4px 6px" }}><div style={{ fontSize:9, color:T.textLight, fontWeight:600, textTransform:"uppercase" }}>{m.l}</div><div style={{ fontSize:11, fontWeight:700, color:T.text }}>{m.v}</div></div>
                      ))}
                    </div>
                    <CardActions
                      onEdit={()=>openEdit("tree",t)}
                      onDelete={()=>deleteAndRefresh(API.deleteTree,rt)(t.id)}
                      onMedia={()=>setMediaModal({ entityType:ENTITY_TYPES.tree, entityId:t.id, title:t.treeId })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── STRATA (read-only) ─── */}
      {tab==="strata"&&(
        <div>
          <div style={{ background:T.primaryLight, border:`1px solid ${T.border}`, borderRadius:10, padding:"11px 14px", marginBottom:16, display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:16, flexShrink:0 }}>📍</span>
            <div style={{ fontSize:12, color:T.primaryDark, lineHeight:1.6 }}>
              <strong>Strata are determined automatically</strong> by the backend based on GPS boundary overlap between this plot and project-level strata definitions.
              A single plot can overlap with multiple strata. To add or edit strata, use the <strong>Strata</strong> top-level tab or the project's Strata tab.
            </div>
          </div>
          {strata.length===0 ? <EmptyState icon="🧱" message="No overlapping strata detected for this plot"/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(290px,1fr))", gap:14 }}>
              {strata.map((s,idx)=>(
                <EntityCard key={s.id}
                  onClick={()=>onNavigate("stratum-detail",s)}
                  color={STRATA_COLORS[idx%STRATA_COLORS.length]}
                  icon="🧱"
                  topLeft={<Badge color={s.qcStatus==="passed"?"green":"yellow"}>QC {s.qcStatus?.toUpperCase()||"PENDING"}</Badge>}
                  topRight={<Badge color="blue">OVERLAPPING</Badge>}
                  subtitle={`${s.areaHectares||"?"} ha`}
                  title={s.stratumName}
                  metrics={[{ l:"Avg Age", v:s.averageTreeAgeYears?`${s.averageTreeAgeYears} yr`:"—" },{ l:"Soil", v:s.predominantSoilType||"—" }]}
                  highlight={null}
                  footer={<div style={{ fontSize:11, color:T.textLight }}>🌿 {s.dominantSpecies||"—"}</div>}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── SOIL SAMPLES ─── */}
      {tab==="soil"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>{soilSamples.length} Soil Sample{soilSamples.length!==1?"s":""}</div>
            <Btn icon="+" onClick={()=>openAdd("soil")}>Add Sample</Btn>
          </div>
          {soilSamples.length>1&&(
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
              <ChartCard title="SOC % by Sample" height={185}>
                <ResponsiveContainer width="100%" height="100%"><BarChart data={soilSamples}><CartesianGrid strokeDasharray="3 3" stroke={T.borderLight}/><XAxis dataKey="sampleId" tick={{ fontSize:10 }}/><YAxis tick={{ fontSize:11 }}/><Tooltip contentStyle={{ fontSize:12, borderRadius:8 }}/><Bar dataKey="soilOrganicCarbonPercent" name="SOC %" fill={T.primary} radius={[4,4,0,0]}/></BarChart></ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Soil Health Radar" height={185}>
                <ResponsiveContainer width="100%" height="100%"><RadarChart data={soilRadarData}><PolarGrid stroke={T.borderLight}/><PolarAngleAxis dataKey="metric" tick={{ fontSize:10 }}/><Radar dataKey="value" stroke={T.primary} fill={T.primary} fillOpacity={0.2}/></RadarChart></ResponsiveContainer>
              </ChartCard>
            </div>
          )}
          {lss ? <LoadingOverlay/> : soilSamples.length===0 ? <EmptyState icon="⚗️" message="No soil samples yet" action={<Btn icon="+" onClick={()=>openAdd("soil")}>Add First Sample</Btn>}/> : (
            <DataTable
              columns={[
                { key:"sampleId",label:"ID" },
                { key:"samplingDate",label:"Date" },
                { key:"depth",label:"Depth",render:v=><Badge color="blue">{v||"—"} cm</Badge> },
                { key:"soilOrganicCarbonPercent",label:"SOC %",render:v=><strong style={{ color:T.success }}>{v||"—"}</strong> },
                { key:"bulkDensityGPerCm3",label:"Bulk Density" },
                { key:"ph",label:"pH" },
                { key:"nitrogenPercent",label:"N %" },
                { key:"phosphorusPpm",label:"P (ppm)" },
                { key:"labMethod",label:"Method" },
              ]}
              data={soilSamples}
              actions={row=>(
                <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                  {/* ✅ Per-sample media button — opens modal with row.id */}
                  <button
                    onClick={()=>setMediaModal({ entityType:ENTITY_TYPES.soil, entityId:row.id, title:row.sampleId })}
                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:T.primary, padding:"2px 5px" }}
                    title="Photos & Documents"
                  >📷</button>
                  <button onClick={()=>openEdit("soil",row)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:13,color:T.primaryDark,padding:"2px 5px" }} title="Edit">✏️</button>
                  <DeleteBtn onDelete={()=>deleteAndRefresh(API.deleteSoilSample,rss)(row.id)} label=""/>
                </div>
              )}
            />
          )}
        </div>
      )}

      {/* ─── CARBON STOCK ─── */}
      {tab==="carbon"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Carbon Stock Calculations ({carbonCalcs.length})</div>
            <Btn icon="+" onClick={()=>openAdd("carbon")}>New Calculation</Btn>
          </div>
          {latestCarbon&&(
            <div style={{ display:"flex", gap:11, flexWrap:"wrap", marginBottom:16 }}>
              <StatCard icon="🌿" label="Total Stock" value={`${latestCarbon.totalCarbonStockTcPerHa} tC/ha`} color={T.success}/>
              <StatCard icon="🌍" label="Net CO₂e/ha" value={latestCarbon.netTco2ePerHa||"—"} color={T.primary}/>
              <StatCard icon="⚖️" label="Uncertainty" value={latestCarbon.uncertaintyPercent?`${latestCarbon.uncertaintyPercent}%`:"—"} color={T.warning}/>
              <StatCard icon="🏆" label="Adj. Credits" value={latestCarbon.uncertaintyAdjustedCredits?.toLocaleString()||"—"} color={T.success}/>
            </div>
          )}
          {lcc ? <LoadingOverlay/> : carbonCalcs.length===0 ? <EmptyState icon="📊" message="No calculations yet" action={<Btn icon="+" onClick={()=>openAdd("carbon")}>New Calculation</Btn>}/> : (
            <DataTable
              columns={[
                { key:"calculationDate",label:"Date" },
                { key:"aboveGroundBiomassTcPerHa",label:"AGB" },
                { key:"belowGroundBiomassTcPerHa",label:"BGB" },
                { key:"soilOrganicCarbonTcPerHa",label:"SOC" },
                { key:"totalCarbonStockTcPerHa",label:"Total (tC/ha)",render:v=><strong style={{ color:T.success }}>{v}</strong> },
                { key:"netTco2ePerHa",label:"Net tCO₂e/ha" },
                { key:"uncertaintyPercent",label:"Uncertainty",render:v=>v?<Badge color={v<10?"green":"yellow"}>{v}%</Badge>:"—" },
                { key:"uncertaintyAdjustedCredits",label:"Adj. Credits",render:v=>v?.toLocaleString() },
                { key:"methodology",label:"Methodology" },
              ]}
              data={carbonCalcs}
              actions={row=>(
                <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                  {/* ✅ Per-calculation media button — opens modal with row.id */}
                  <button
                    onClick={()=>setMediaModal({ entityType:ENTITY_TYPES.carbon, entityId:row.id, title:`Calc ${row.calculationDate}` })}
                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:T.primary, padding:"2px 5px" }}
                    title="Photos & Documents"
                  >📷</button>
                  <button onClick={()=>openEdit("carbon",row)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:13,color:T.primaryDark,padding:"2px 5px" }} title="Edit">✏️</button>
                  <DeleteBtn onDelete={()=>deleteAndRefresh(API.deleteCarbonStock,rcc)(row.id)} label=""/>
                </div>
              )}
            />
          )}
        </div>
      )}

      {/* ─── MANAGEMENT ─── */}
      {tab==="management"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Management Activities ({activities.length})</div>
            <Btn icon="+" onClick={()=>openAdd("mgmt")}>Log Activity</Btn>
          </div>
          {lma ? <LoadingOverlay/> : activities.length===0 ? <EmptyState icon="🔧" message="No activities logged" action={<Btn icon="+" onClick={()=>openAdd("mgmt")}>Log First Activity</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(275px,1fr))", gap:12 }}>
              {activities.map(a=>(
                <div key={a.id} style={{ background:T.card, borderRadius:10, border:`1px solid ${T.borderLight}`, overflow:"hidden" }}>
                  <div style={{ height:42, background:`linear-gradient(135deg, ${T.primaryDeep}, ${T.primary})`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 12px" }}>
                    <div style={{ display:"flex", gap:5 }}>
                      {a.activityType&&<Badge color={a.activityType==="PLANTING"?"green":a.activityType==="FERTILIZATION"?"blue":"yellow"}>{a.activityType}</Badge>}
                      {a.expectedCarbonImpact&&<Badge color={a.expectedCarbonImpact==="POSITIVE"?"green":a.expectedCarbonImpact==="NEGATIVE"?"red":"gray"}>{a.expectedCarbonImpact}</Badge>}
                    </div>
                  </div>
                  <div style={{ padding:"10px 13px" }}>
                    {a.description&&<div style={{ fontSize:12, color:T.text, fontWeight:600, marginBottom:7 }}>{a.description}</div>}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, marginBottom:7 }}>
                      {[{ l:"Date", v:a.activityDate },{ l:"Area", v:a.areaAffectedHectares?`${a.areaAffectedHectares} ha`:"—" },{ l:"Cost", v:a.costInInr?`₹${Number(a.costInInr).toLocaleString()}`:"—" },{ l:"C Retention", v:a.estimatedCarbonRetentionPercent?`${a.estimatedCarbonRetentionPercent}%`:"—" }].map((m,i)=>(
                        <div key={i} style={{ background:T.bg, borderRadius:5, padding:"4px 7px" }}><div style={{ fontSize:9, color:T.textLight, fontWeight:600, textTransform:"uppercase" }}>{m.l}</div><div style={{ fontSize:11, fontWeight:700, color:T.text }}>{m.v||"—"}</div></div>
                      ))}
                    </div>
                    {a.materialsUsed&&<div style={{ fontSize:11, color:T.textLight, marginBottom:6 }}>🔩 {a.materialsUsed}</div>}
                    <CardActions
                      onEdit={()=>openEdit("mgmt",a)}
                      onDelete={()=>deleteAndRefresh(API.deleteManagementActivity,rma)(a.id)}
                      onMedia={()=>setMediaModal({ entityType:ENTITY_TYPES.mgmt, entityId:a.id, title:a.activityType||"Activity" })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── HARVEST ─── */}
      {tab==="harvest"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Harvest Records ({harvests.length})</div>
            <Btn icon="+" onClick={()=>openAdd("harvest")}>Add Record</Btn>
          </div>
          {lh ? <LoadingOverlay/> : harvests.length===0 ? <EmptyState icon="🌾" message="No harvest records yet" action={<Btn icon="+" onClick={()=>openAdd("harvest")}>Add Record</Btn>}/> : (
            <DataTable
              columns={[
                { key:"speciesHarvested",label:"Species",render:v=><span style={{ fontStyle:"italic" }}>{v}</span> },
                { key:"harvestDate",label:"Date" },
                { key:"productType",label:"Product" },
                { key:"volumeOrWeight",label:"Volume / Weight",render:(v,r)=>`${v||"—"} ${r.unit||""}` },
                { key:"longTermStorageFractionPercent",label:"LT Storage",render:v=><Badge color={v>50?"green":v>0?"yellow":"gray"}>{v||0}%</Badge> },
                { key:"notes",label:"Notes" },
              ]}
              data={harvests}
              actions={row=>(
                <div style={{ display:"flex", gap:4, alignItems:"center" }}>
                  {/* ✅ Per-harvest media button — opens modal with row.id */}
                  <button
                    onClick={()=>setMediaModal({ entityType:ENTITY_TYPES.harvest, entityId:row.id, title:row.speciesHarvested })}
                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:14, color:T.primary, padding:"2px 5px" }}
                    title="Photos & Documents"
                  >📷</button>
                  <button onClick={()=>openEdit("harvest",row)} style={{ background:"none",border:"none",cursor:"pointer",fontSize:13,color:T.primaryDark,padding:"2px 5px" }} title="Edit">✏️</button>
                  <DeleteBtn onDelete={()=>deleteAndRefresh(API.deleteHarvestRecord,rh)(row.id)} label=""/>
                </div>
              )}
            />
          )}
        </div>
      )}

      {/* ─── MONITORING ─── */}
      {tab==="monitoring"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Monitoring Schedules ({schedules.length})</div>
            <Btn icon="+" onClick={()=>openAdd("monitoring")}>Schedule Activity</Btn>
          </div>
          {lm ? <LoadingOverlay/> : schedules.length===0 ? <EmptyState icon="📅" message="No monitoring scheduled" action={<Btn icon="+" onClick={()=>openAdd("monitoring")}>Schedule First</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(255px,1fr))", gap:11 }}>
              {schedules.map(m=>(
                <div key={m.id} style={{ background:T.card, borderRadius:10, border:`2px solid`, borderColor:m.status==="COMPLETED"?"#b8e8cc":m.status==="MISSED"?"#f0b8b4":m.status==="RESCHEDULED"?"#f5d08a":T.borderLight, padding:"12px 14px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:7 }}>
                    {m.monitoringType&&<Badge color="blue">{m.monitoringType}</Badge>}
                    {m.status&&<Badge color={m.status==="COMPLETED"?"green":m.status==="MISSED"?"red":m.status==="RESCHEDULED"?"yellow":"gray"}>{m.status}</Badge>}
                  </div>
                  <div style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:4 }}>👤 {m.personnelResponsible||"—"}</div>
                  <div style={{ fontSize:11, color:T.textMid, marginBottom:2 }}>📅 Scheduled: {m.scheduledDate||"—"}</div>
                  {m.actualDate&&<div style={{ fontSize:11, color:T.success }}>✅ Actual: {m.actualDate}</div>}
                  {m.notes&&<div style={{ fontSize:11, color:T.textLight, fontStyle:"italic", marginTop:4 }}>{m.notes}</div>}
                  <CardActions
                    onEdit={()=>openEdit("monitoring",m)}
                    onDelete={()=>deleteAndRefresh(API.deleteMonitoringSchedule,rm)(m.id)}
                    onMedia={()=>setMediaModal({ entityType:ENTITY_TYPES.mgmt, entityId:m.id, title:m.monitoringType||"Schedule" })}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── EMISSION LEAKAGES ─── */}
      {tab==="leakage"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Emission Assessments ({leakages.length})</div>
            <Btn icon="+" onClick={()=>openAdd("leakage")}>Add Assessment</Btn>
          </div>
          {leakages.length>0&&(
            <div style={{ display:"flex", gap:11, flexWrap:"wrap", marginBottom:16 }}>
              <StatCard icon="💨" label="Total Estimated Emissions" value={`${leakages.reduce((s,l)=>s+Number(l.estimatedEmissionsTco2e||0),0).toFixed(2)} tCO₂e`} color={T.danger}/>
              <StatCard icon="📉" label="Total Leakage Estimate" value={`${leakages.reduce((s,l)=>s+Number(l.leakageEstimateTco2e||0),0).toFixed(2)} tCO₂e`} color={T.warning}/>
            </div>
          )}
          {ll ? <LoadingOverlay/> : leakages.length===0 ? <EmptyState icon="💨" message="No emission assessments recorded" action={<Btn icon="+" onClick={()=>openAdd("leakage")}>Add First Assessment</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(310px,1fr))", gap:12 }}>
              {leakages.map(l=>(
                <div key={l.id} style={{ background:T.card, borderRadius:11, border:`1px solid ${T.borderLight}`, overflow:"hidden" }}>
                  <div style={{ height:42, background:`linear-gradient(135deg,${T.danger},#e8796e)`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 13px" }}>
                    <div style={{ display:"flex", gap:5 }}>
                      {l.emissionSource&&<Badge color="red">{l.emissionSource}</Badge>}
                    </div>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.75)", fontWeight:600 }}>{l.emissionDate}</span>
                  </div>
                  <div style={{ padding:"11px 14px" }}>
                    {l.activityDescription&&<div style={{ fontSize:12, fontWeight:600, color:T.text, marginBottom:9 }}>{l.activityDescription}</div>}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, marginBottom:7 }}>
                      {[
                        { l:"Fuel / Material", v:l.fuelOrMaterialType||"—" },
                        { l:"Quantity", v:l.quantity?`${l.quantity} ${l.unit||""}`:("—") },
                        { l:"Est. Emissions", v:l.estimatedEmissionsTco2e?`${l.estimatedEmissionsTco2e} tCO₂e`:"—", hi:true },
                        { l:"Leakage Est.", v:l.leakageEstimateTco2e!=null?`${l.leakageEstimateTco2e} tCO₂e`:"—" },
                      ].map((m,i)=>(
                        <div key={i} style={{ background:m.hi?T.dangerLight:T.bg, borderRadius:6, padding:"5px 8px", border:m.hi?`1px solid #f0b8b4`:"none" }}>
                          <div style={{ fontSize:9, color:T.textLight, fontWeight:600, textTransform:"uppercase" }}>{m.l}</div>
                          <div style={{ fontSize:12, fontWeight:700, color:m.hi?T.danger:T.text }}>{m.v}</div>
                        </div>
                      ))}
                    </div>
                    {l.notes&&<div style={{ fontSize:11, color:T.textLight, fontStyle:"italic", marginBottom:6 }}>📝 {l.notes}</div>}
                    <CardActions
                      onEdit={()=>openEdit("leakage",l)}
                      onDelete={()=>deleteAndRefresh(API.deleteEmissionLeakage,rl)(l.id)}
                      onMedia={()=>setMediaModal({ entityType:ENTITY_TYPES.leakage, entityId:l.id, title:l.emissionSource||"Assessment" })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── BIODIVERSITY ─── */}
      {tab==="biodiversity"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Biodiversity Observations ({bioObs.length})</div>
            <Btn icon="+" onClick={()=>openAdd("bio")}>Add Observation</Btn>
          </div>
          {lb ? <LoadingOverlay/> : bioObs.length===0 ? <EmptyState icon="🦋" message="No observations recorded" action={<Btn icon="+" onClick={()=>openAdd("bio")}>Add First Observation</Btn>}/> : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(265px,1fr))", gap:11 }}>
              {bioObs.map(b=>(
                <div key={b.id} style={{ background:T.card, borderRadius:10, border:`1px solid ${T.borderLight}`, overflow:"hidden" }}>
                  <div style={{ height:40, background:`linear-gradient(135deg,${b.indicatorType==="BIRDS"?T.primary:b.indicatorType==="FLORA"?T.success:b.indicatorType==="INSECTS"?T.warning:"#7c4dbc"},${T.primaryMid})`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 11px" }}>
                    {b.indicatorType&&<Badge color={b.indicatorType==="BIRDS"?"blue":b.indicatorType==="FLORA"?"green":b.indicatorType==="INSECTS"?"yellow":"gray"}>{b.indicatorType}</Badge>}
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>{b.observationDate}</span>
                  </div>
                  <div style={{ padding:"10px 13px" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.text, fontStyle:"italic", marginBottom:4 }}>{b.speciesObserved}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                      {b.assessmentMethod&&<span style={{ fontSize:11, color:T.textMid }}>Method: {b.assessmentMethod}</span>}
                      {b.countOrAbundance!=null&&<span style={{ fontSize:12, fontWeight:700, color:T.success }}>Count: {b.countOrAbundance}</span>}
                    </div>
                    {b.notes&&<div style={{ fontSize:11, color:T.textLight, fontStyle:"italic", marginBottom:6 }}>{b.notes}</div>}
                    <CardActions
                      onEdit={()=>openEdit("bio",b)}
                      onDelete={()=>deleteAndRefresh(API.deleteBiodiversityObservation,rb)(b.id)}
                      onMedia={()=>setMediaModal({ entityType:ENTITY_TYPES.bio, entityId:b.id, title:b.speciesObserved||"Observation" })}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── STRATUM QC ─── */}
      {tab==="stratumqc"&&(
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:13 }}>
            <div style={{ fontSize:14, fontWeight:700, color:T.text }}>Stratum QC Assessments ({qcList.length})</div>
            <Btn icon="+" onClick={()=>openAdd("qc")}>New QC Check</Btn>
          </div>
          {lq ? <LoadingOverlay/> : qcList.length===0 ? <EmptyState icon="✅" message="No QC assessments yet" action={<Btn icon="+" onClick={()=>openAdd("qc")}>Add QC Check</Btn>}/> : (
            qcList.map((qc,i)=>(
              <div key={qc.id} style={{ background:T.card, borderRadius:11, border:`2px solid`, borderColor:qc.completenessChecklistPassed?"#b8e8cc":"#f0b8b4", marginBottom:11, overflow:"hidden" }}>
                <div style={{ height:34, background:`linear-gradient(135deg,${qc.completenessChecklistPassed?T.success:T.danger},${qc.completenessChecklistPassed?"#52c988":"#e8796e"})`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 16px" }}>
                  <span style={{ color:"#fff", fontSize:12, fontWeight:700 }}>Assessment #{i+1} · {qc.assessmentDate}</span>
                  <Badge color={qc.completenessChecklistPassed?"green":"red"}>{qc.completenessChecklistPassed?"✓ PASSED":"✗ FAILED"}</Badge>
                </div>
                <div style={{ padding:"13px 16px" }}>
                  <div style={{ display:"flex", gap:12, marginBottom:10 }}>
                    <div style={{ background:T.bg, borderRadius:7, padding:"8px 14px", textAlign:"center", flexShrink:0 }}>
                      <div style={{ fontSize:9, color:T.textLight, fontWeight:600, textTransform:"uppercase" }}>Uncertainty</div>
                      <div style={{ fontSize:18, fontWeight:800, color:qc.uncertaintyPercent<10?T.success:T.warning }}>{qc.uncertaintyPercent!=null?`${qc.uncertaintyPercent}%`:"—"}</div>
                    </div>
                    <div style={{ flex:1 }}>
                      {qc.systemFlags&&<div style={{ background:T.dangerLight, borderRadius:7, padding:"7px 10px", marginBottom:6, border:`1px solid #f0b8b4` }}><div style={{ fontSize:11, fontWeight:700, color:T.danger, marginBottom:2 }}>⚑ System Flags</div><div style={{ fontSize:12, color:T.danger }}>{qc.systemFlags}</div></div>}
                      {qc.notes&&<div style={{ fontSize:12, color:T.textMid, fontStyle:"italic" }}>📝 {qc.notes}</div>}
                    </div>
                  </div>
                  <CardActions
                    onEdit={()=>openEdit("qc",qc)}
                    onDelete={()=>deleteAndRefresh(API.deleteStratumQC,rq)(qc.id)}
                    onMedia={()=>setMediaModal({ entityType:ENTITY_TYPES.qc, entityId:qc.id, title:`Assessment #${i+1}` })}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   STRATUM DETAIL
═══════════════════════════════════════════════════════════════════ */
const StratumDetailView = ({ stratum, onNavigate }) => {
  const { data:plots } = useApiData(()=>API.getPlots(stratum.projectId),[stratum.projectId]);
  const { data:projects } = useApiData(()=>API.getProjects());
  const { data:qcList, loading:lq, refresh:rq } = useApiData(()=>API.getStratumQCByStratum(stratum.id),[stratum.id]);
  const [showQCForm, setShowQCForm] = useState(false);
  const [editQC, setEditQC] = useState(null);
  const [showStratumEdit, setShowStratumEdit] = useState(false);
  // ─── Media modal for per-QC photos & docs ───
  const [mediaModal, setMediaModal] = useState(null);

  const closeQCForm = () => { setShowQCForm(false); setEditQC(null); };
  const handleDeleteQC = async (id) => { await API.deleteStratumQC(id); rq(); };

  return (
    <div>
      <Modal open={showStratumEdit} onClose={()=>setShowStratumEdit(false)} title="Edit Stratum" subtitle="Update stratum details" width={640}>
        <StratumForm onClose={()=>setShowStratumEdit(false)} onRefresh={()=>{}} projects={projects} initialData={stratum}/>
      </Modal>
      <Modal open={showQCForm||!!editQC} onClose={closeQCForm}
        title={editQC?"Edit QC Assessment":"New Stratum QC Assessment"} width={640}>
        <StratumQCForm onClose={closeQCForm} onRefresh={rq} preselectedStratumId={stratum.id} initialData={editQC}/>
      </Modal>

      {/* Per-QC media modal */}
      <EntityMediaModal modal={mediaModal} onClose={() => setMediaModal(null)} />

      <Breadcrumb crumbs={[{ label:"Projects", onClick:()=>onNavigate("projects") },{ label:stratum.project?.officialProjectName||`Project #${stratum.projectId}`, onClick:()=>onNavigate("project-detail",{ id:stratum.projectId, officialProjectName:stratum.project?.officialProjectName }) },{ label:`Stratum: ${stratum.stratumName}` }]}/>
      <div style={{ background:`linear-gradient(135deg, ${T.primaryDeep} 0%, ${T.primary} 100%)`, borderRadius:14, marginBottom:20, overflow:"hidden" }}>
        <div style={{ padding:"18px 24px 12px", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontSize:10, opacity:0.62, fontWeight:600, letterSpacing:1, marginBottom:3, color:"#fff" }}>STRATUM · {stratum.project?.officialProjectName||`Project #${stratum.projectId}`}</div>
            <h2 style={{ margin:"0 0 4px", fontSize:19, fontWeight:800, fontFamily:"Syne, sans-serif", color:"#fff" }}>{stratum.stratumName}</h2>
            <div style={{ opacity:0.76, fontSize:12, color:"#fff" }}>🌿 {stratum.dominantSpecies||"—"}</div>
          </div>
          <button onClick={()=>setShowStratumEdit(true)} style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 13px", borderRadius:8, border:"1px solid rgba(255,255,255,0.4)", background:"rgba(255,255,255,0.15)", cursor:"pointer", color:"#fff", fontSize:12, fontWeight:600, fontFamily:"inherit", backdropFilter:"blur(4px)", flexShrink:0 }}>
            ✏️ Edit Stratum
          </button>
        </div>
      </div>

      {/* ✅ Stratum-level images — keyed to stratum.id */}
      <ImageGallery entityType={ENTITY_TYPES.stratum} entityId={stratum.id}/>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20, alignItems:"stretch" }}>
        <div style={{ background:T.card, borderRadius:12, padding:"16px 18px", border:`1px solid ${T.borderLight}`, boxShadow:"0 1px 8px rgba(63,136,165,0.06)" }}>
          <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:12 }}>Stratum Details</div>
          <InfoGrid items={[
            { label:"Stratum Name", value:stratum.stratumName },{ label:"Area", value:stratum.areaHectares?`${stratum.areaHectares} ha`:"—" },
            { label:"Avg Tree Age", value:stratum.averageTreeAgeYears?`${stratum.averageTreeAgeYears} years`:"—" },{ label:"Soil Type", value:stratum.predominantSoilType||"—" },
            { label:"Dominant Species", value:stratum.dominantSpecies||"—" },{ label:"QC Status", value:stratum.qcStatus?.toUpperCase()||"PENDING" },
          ]}/>
        </div>
        <MapCard title="Stratum Boundary" subtitle="GPS polygon boundary (SRID 4326)" label={stratum.stratumName} boundary={stratum.gpsBoundary} centroid={stratum.centroid} height={240}/>
      </div>

      {/* QC History */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.text }}>QC Assessment History ({qcList.length})</div>
        <Btn icon="+" onClick={()=>setShowQCForm(true)}>New QC Check</Btn>
      </div>
      {lq ? <LoadingOverlay/> : qcList.length===0 ? <EmptyState icon="✅" message="No QC assessments yet" action={<Btn icon="+" onClick={()=>setShowQCForm(true)}>Add QC Check</Btn>}/> : (
        qcList.map((qc,i)=>(
          <div key={qc.id} style={{ background:T.card, borderRadius:11, border:`2px solid`, borderColor:qc.completenessChecklistPassed?"#b8e8cc":"#f0b8b4", marginBottom:11 }}>
            <div style={{ padding:"10px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${T.borderLight}` }}>
              <span style={{ fontSize:12, fontWeight:700, color:T.text }}>Assessment #{i+1} · {qc.assessmentDate}</span>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <Badge color={qc.completenessChecklistPassed?"green":"red"}>{qc.completenessChecklistPassed?"✓ PASSED":"✗ FAILED"}</Badge>
                <button
                  onClick={()=>setMediaModal({ entityType:ENTITY_TYPES.qc, entityId:qc.id, title:`Assessment #${i+1}` })}
                  style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 9px", borderRadius:6, border:`1px solid ${T.border}`, background:"#fff", cursor:"pointer", fontSize:11, color:T.primary, fontWeight:600, fontFamily:"inherit" }}
                  title="Photos & Documents"
                >📷 Media</button>
                <button onClick={()=>setEditQC(qc)} style={{ display:"flex", alignItems:"center", gap:4, padding:"3px 9px", borderRadius:6, border:`1px solid ${T.border}`, background:"#fff", cursor:"pointer", fontSize:11, color:T.primaryDark, fontWeight:600, fontFamily:"inherit" }}>✏️ Edit</button>
                <DeleteBtn onDelete={()=>handleDeleteQC(qc.id)} label=""/>
              </div>
            </div>
            <div style={{ padding:"11px 16px" }}>
              {qc.uncertaintyPercent!=null&&<div style={{ fontSize:12, color:T.textMid, marginBottom:4 }}>Uncertainty: <strong style={{ color:qc.uncertaintyPercent<10?T.success:T.warning }}>{qc.uncertaintyPercent}%</strong></div>}
              {qc.systemFlags&&<div style={{ fontSize:11, color:T.danger, marginBottom:4 }}>⚑ {qc.systemFlags}</div>}
              {qc.notes&&<div style={{ fontSize:12, color:T.textMid, fontStyle:"italic" }}>📝 {qc.notes}</div>}
            </div>
          </div>
        ))
      )}

      <div style={{ marginTop:18 }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:11 }}>Plots in this Project</div>
        <DataTable
          columns={[{ key:"plotId",label:"Plot ID" },{ key:"landUseType",label:"Land Use",render:v=>v?<Badge color={v==="FOREST"?"green":v==="AGRICULTURE"?"yellow":"blue"}>{v}</Badge>:"—" },{ key:"areaHectares",label:"Area (ha)" },{ key:"vegetationType",label:"Vegetation" },{ key:"speciesComposition",label:"Species" }]}
          data={plots}
          onRowClick={p=>onNavigate("plot-detail",p)}
        />
      </div>
      {/* ✅ Stratum-level documents — keyed to stratum.id */}
      <DocumentsSection entityType={ENTITY_TYPES.stratum} entityId={stratum.id}/>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   ROOT APP
═══════════════════════════════════════════════════════════════════ */
const ADD_BTN = {
  projects: { label:"New Project",  icon:"+" },
  plots:    { label:"New Plot",     icon:"+" },
  strata:   { label:"New Stratum",  icon:"+" },
};

export default function CarbonSequestrationModule(props) {
  const [view, setView]       = useState("projects");
  const [entity, setEntity]   = useState(null);
  const [addModal, setAddModal] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const navigate  = (v, e=null) => { setView(v); if(e) setEntity(e); };
  const activeTab = ["projects","plots","strata"].includes(view) ? view : "projects";
  const isDetailView = ["project-detail","plot-detail","stratum-detail"].includes(view);

  const PAGE_META = {
    projects:  { title:"Farm Projects",           sub:"Manage and monitor all carbon projects" },
    plots:     { title:"All Plots",               sub:"35 plots · 2,820 ha across 4 projects" },
    strata:    { title:"All Strata",              sub:"15 strata across 4 projects" },
  };
  const meta = PAGE_META[activeTab] || PAGE_META.projects;

  return (
    <div className="d-flex flex-row mainclass" style={{ height: "100vh", overflow: "auto" }}>
      <div style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px", position: "sticky", top: 0, zIndex: 999 }}>
        <Sidebar dataFromParent={props.location.pathname} onSidebarToggle={setSidebarExpanded} />
      </div>

      <div style={{ flex: "1", transition: "all 0.3s ease" }}>
        <div style={{ position: "sticky", top: 0, zIndex: 999 }}><Header /></div>
        <div className="content-wrapper">
          {!isDetailView && (
            <div className="page-header">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h1>{meta.title}</h1>
                  <p>{meta.sub}</p>
                </div>
                {ADD_BTN[activeTab] && (
                  <Btn icon={ADD_BTN[activeTab].icon} onClick={()=>setAddModal(activeTab)}>
                    {ADD_BTN[activeTab].label}
                  </Btn>
                )}
              </div>
            </div>
          )}
          {!isDetailView && (
            <div style={{ display:"flex", borderBottom:`2px solid ${T.border}`, gap:2, overflowX:"auto", marginBottom:24 }}>
              {TOP_TABS.map(tab => (
                <button key={tab.id} onClick={() => navigate(tab.id)}
                  style={{ display:"flex", alignItems:"center", gap:7, padding:"9px 18px", border:"none",
                    borderBottom:`2px solid ${activeTab===tab.id ? T.primary : "transparent"}`,
                    marginBottom:-2, background:"transparent", cursor:"pointer", fontFamily:"inherit",
                    fontWeight:activeTab===tab.id ? 700 : 500, fontSize:13,
                    color:activeTab===tab.id ? T.primary : T.textLight,
                    transition:"all 0.15s", whiteSpace:"nowrap" }}>
                  <span style={{ fontSize:14 }}>{tab.icon}</span>{tab.label}
                </button>
              ))}
            </div>
          )}
          {view==="projects"       && <ProjectsView     onNavigate={navigate} hideHeader/>}
          {view==="plots"          && <AllPlotsView     onNavigate={navigate} hideHeader/>}
          {view==="strata"         && <AllStrataView    onNavigate={navigate} hideHeader/>}
          {view==="project-detail" && entity && <ProjectDetailView project={entity}  onNavigate={navigate}/>}
          {view==="plot-detail"    && entity && <PlotDetailView    plot={entity}      onNavigate={navigate}/>}
          {view==="stratum-detail" && entity && <StratumDetailView stratum={entity}   onNavigate={navigate}/>}
        </div>

        <Modal open={addModal==="projects"} onClose={()=>setAddModal(null)} title="New Farm Project" subtitle="Register a new carbon project" width={700}>
          <ProjectForm onClose={()=>setAddModal(null)}/>
        </Modal>
        <Modal open={addModal==="plots"} onClose={()=>setAddModal(null)} title="New Plot" subtitle="Add a plot to a project" width={680}>
          <PlotForm onClose={()=>setAddModal(null)}/>
        </Modal>
        <Modal open={addModal==="strata"} onClose={()=>setAddModal(null)} title="New Stratum" subtitle="Define a stratum for a project" width={640}>
          <StratumForm onClose={()=>setAddModal(null)}/>
        </Modal>
      </div>
    </div>
  );
}